import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../module_roots/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    faEdit, faTrash, faSave, faSearch, faRefresh, faSpinner, faCalculator, faFileInvoice, faMoneyBill, faBalanceScale, faBoxes,
    faExclamationTriangle, faCheckCircle, faTimes, faChevronLeft,
    faChevronRight, faFilePdf, faCalendar, faUser, faStore, faPhone,
    faEnvelope, faMapMarkerAlt, faTag, faExchangeAlt, faChevronDown,
    faExpand, faCompress, faArrowsAlt, faMinus, faPlus, faFilter, faEyeSlash,
    faShoppingCart, faEye, faPrint, faDollarSign, faCreditCard, faWallet,
    faReceipt, faHistory, faInfoCircle, faBox, faBarcode, faBuilding, faLayerGroup, faCubes
} from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Helper function to parse bquantity numeric value
const parseBQuantityNumeric = (bquantityStr) => {
    if (!bquantityStr) return 0;
    const match = bquantityStr.match(/^([\d,\.]+)/);
    if (match) return parseFloat(match[1].replace(/,/g, ''));
    return 0;
};

// Helper function to adjust color brightness
const adjustColor = (color, amount) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) return '#6366f1';
    let r = parseInt(color.slice(1, 3), 16);
    let g = parseInt(color.slice(3, 5), 16);
    let b = parseInt(color.slice(5, 7), 16);
    r = Math.max(0, Math.min(255, r + amount));
    g = Math.max(0, Math.min(255, g + amount));
    b = Math.max(0, Math.min(255, b + amount));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

// Helper function to escape HTML
function escapeHtml(text) {
    if (!text) return '';
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
}

// Helper function to get payment status text
function getPaymentStatusText(paid, grandTotal) {
    const paidAmount = parseFloat(paid) || 0;
    const totalAmount = parseFloat(grandTotal) || 0;
    const balance = totalAmount - paidAmount;
    if (balance <= 0) return 'PAID';
    if (paidAmount === 0) return 'UNPAID';
    return 'PARTIAL';
}

// Helper function to parse packaging info from purchase items
const parseItemPackagingInfo = (item) => {
    if (item.packaging_info) {
        try {
            if (typeof item.packaging_info === 'string') {
                return JSON.parse(item.packaging_info);
            }
            return item.packaging_info;
        } catch (e) {
            return null;
        }
    }
    return null;
};

// Helper function to calculate profit price
const calculateProfitPrice = (costPerPack, profitPercentage) => {
    const cost = parseFloat(costPerPack) || 0;
    const profit = parseFloat(profitPercentage) || 0;
    if (cost <= 0 || profit <= 0) return '';
    return (cost + (cost * profit / 100)).toFixed(2);
};

// Helper function to get display text for packaging fields
const getPackagingDisplay = (item, field) => {
    if (item[field] && item[field] !== '') {
        if (field === 'cost_per_pack' || field === 'sell_per_pack') {
            return `Rs. ${parseFloat(item[field]).toFixed(2)}`;
        }
        return item[field];
    }
    const packagingInfo = parseItemPackagingInfo(item);
    if (packagingInfo) {
        switch (field) {
            case 'packing_type': return packagingInfo.packing_type || '-';
            case 'sub_packing_type': return packagingInfo.sub_packing_type || '-';
            case 'items_per_pack': return packagingInfo.items_per_pack || '-';
            case 'unit_of_measure': return packagingInfo.unit_of_measure || '-';
            case 'cost_per_pack': return packagingInfo.cost_per_pack ? `Rs. ${parseFloat(packagingInfo.cost_per_pack).toFixed(2)}` : '-';
            case 'sell_per_pack': return packagingInfo.sell_per_pack ? `Rs. ${parseFloat(packagingInfo.sell_per_pack).toFixed(2)}` : '-';
            default: return '-';
        }
    }
    return '-';
};

// Create translator instance
const createPurchasesListTranslator = () => {
    let cache = new Map();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) { }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
        } catch (e) { }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'Purchase List': 'خریداری کی فہرست',
                'All purchase details are below': 'تمام خریداری کی تفصیلات نیچے ہیں',
                'Bill Number': 'بل نمبر',
                'Supplier Name': 'سپلائر کا نام',
                'Phone': 'فون نمبر',
                'Date': 'تاریخ',
                'Grand Total': 'کل رقم',
                'Paid Amount': 'ادا کردہ رقم',
                'Balance': 'باقی رقم',
                'Actions': 'اعمال',
                'Added By': 'شامل کرنے والا',
                'Search purchases...': 'خریداری تلاش کریں...',
                'Refresh': 'ریفریش کریں',
                'Export PDF': 'پی ڈی ایف ایکسپورٹ کریں',
                'Edit': 'ترمیم کریں',
                'Delete': 'حذف کریں',
                'View': 'دیکھیں',
                'Print': 'پرنٹ کریں',
                'Close': 'بند کریں',
                'Save Changes': 'تبدیلیاں محفوظ کریں',
                'Cancel': 'منسوخ کریں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Showing': 'دکھا رہا ہے',
                'of': 'میں سے',
                'Previous': 'پچھلا',
                'Next': 'اگلا',
                'Switch Shop': 'شاپ تبدیل کریں',
                'No shop selected': 'کوئی شاپ منتخب نہیں',
                'Please select a shop to view purchases': 'براہ کرم خریداری دیکھنے کے لیے شاپ منتخب کریں',
                'S.No': 'نمبر',
                'Purchases': 'خریداری',
                'Shop': 'شاپ',
                'Purchase Details': 'خریداری کی تفصیلات',
                'Items': 'آئٹمز',
                'Product': 'پروڈکٹ',
                'Quantity': 'مقدار',
                'Price': 'قیمت',
                'Total': 'کل',
                'Subtotal': 'ذیلی کل',
                'Tax': 'ٹیکس',
                'Discount': 'ڈسکاؤنٹ',
                'Update Purchase': 'خریداری اپ ڈیٹ کریں',
                'Updating...': 'اپ ڈیٹ ہو رہا ہے...',
                'Purchase updated successfully': 'خریداری کامیابی سے اپ ڈیٹ ہو گئی',
                'Error updating purchase': 'خریداری اپ ڈیٹ کرنے میں خرابی',
                'Confirm Delete': 'حذف کرنے کی تصدیق',
                'Yes, Delete': 'ہاں، حذف کریں',
                'No, Cancel': 'نہیں، منسوخ کریں',
                'Portrait': 'عمودی',
                'Landscape': 'افقی',
                'A4': 'A4',
                'Letter': 'لیٹر',
                'Legal': 'لیگل',
                'A5': 'A5',
                'Purchase Report': 'خریداری رپورٹ',
                'Total Purchases': 'کل خریداری',
                'Generated': 'تیار کردہ',
                'Report ID': 'رپورٹ آئی ڈی',
                'Generated by': 'تیار کردہ بذریعہ',
                'Shop Code': 'شاپ کوڈ',
                'FILTERS APPLIED': 'فلٹرز لاگو ہیں',
                'Search': 'تلاش کریں',
                'From': 'سے',
                'To': 'تک',
                'Page': 'صفحہ',
                'Paid': 'ادا شدہ',
                'Unpaid': 'غیر ادا شدہ',
                'Partially Paid': 'جزوی ادا شدہ',
                'All': 'سب',
                'Bill Number Filter': 'بل نمبر',
                'Supplier Name Filter': 'سپلائر کا نام',
                'Phone Filter': 'فون نمبر',
                'Added By Filter': 'شامل کرنے والا',
                'Date Range': 'تاریخ کی حد',
                'Start Date': 'شروع تاریخ',
                'End Date': 'اختتام تاریخ',
                'Payment Status Filter': 'ادائیگی کی حیثیت',
                'Filter By': 'فلٹر کریں بذریعہ',
                'Orientation': 'اورینٹیشن',
                'Page Size': 'صفحہ کا سائز',
                'Cash': 'نقد',
                'Card': 'کارڈ',
                'UPI': 'یوپی آئی',
                'Bank Transfer': 'بینک ٹرانسفر',
                'cash': 'نقد',
                'card': 'کارڈ',
                'upi': 'یوپی آئی',
                'bank_transfer': 'بینک ٹرانسفر',
                'Sr#': 'شمارہ',
                'Batch': 'بیچ نمبر',
                'Barcode': 'بار کوڈ',
                'Cost Price': 'لاگت قیمت',
                'Sell Price': 'فروخت قیمت',
                'Stock': 'اسٹاک',
                'Expiry': 'میعاد ختم',
                'Category': 'زمرہ',
                'Purchase Receipt': 'خریداری رسید',
                'Time': 'وقت',
                'Supplier': 'سپلائر',
                'Address': 'پتہ',
                'Thank you for your business!': 'آپ کے کاروبار کا شکریہ!',
                'Powered by OurZest': 'پاورڈ بہ اؤرزسٹ',
                'No data to export': 'برآمد کرنے کے لیے کوئی ڈیٹا نہیں',
                'Report generated!': 'رپورٹ تیار!',
                'pdfGenerationError': 'پی ڈی ایف بنانے میں خرابی',
                'readonlyNotice': 'بل نمبر اور آئٹمز تخلیق کے بعد تبدیل نہیں کیے جا سکتے',
                'purchases': 'خریداریاں',
                'pages': 'صفحات',
                'paymentStatus': 'ادائیگی کی حیثیت',
                'purchaseReceipt': 'خریداری رسید',
                'receipt': 'رسید',
                'notes': 'نوٹس',
                'purchaseId': 'خریداری آئی ڈی',
                'itemBatch': 'بیچ',
                'itemUnit': 'یونٹ'
            },
            'ar': {
                'Purchase List': 'قائمة المشتريات',
                'All purchase details are below': 'جميع تفاصيل المشتريات أدناه',
                'Bill Number': 'رقم الفاتورة',
                'Supplier Name': 'اسم المورد',
                'Phone': 'رقم الهاتف',
                'Date': 'التاريخ',
                'Grand Total': 'المجموع الكلي',
                'Paid Amount': 'المبلغ المدفوع',
                'Balance': 'الرصيد',
                'Actions': 'الإجراءات',
                'Added By': 'أضيف بواسطة',
                'Search purchases...': 'بحث عن مشتريات...',
                'Refresh': 'تحديث',
                'Export PDF': 'تصدير PDF',
                'Edit': 'تعديل',
                'Delete': 'حذف',
                'View': 'عرض',
                'Print': 'طباعة',
                'Close': 'إغلاق',
                'Save Changes': 'حفظ التغييرات',
                'Cancel': 'إلغاء',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Showing': 'عرض',
                'of': 'من',
                'Previous': 'السابق',
                'Next': 'التالي',
                'Switch Shop': 'تبديل المتجر',
                'No shop selected': 'لم يتم اختيار متجر',
                'Please select a shop to view purchases': 'الرجاء اختيار متجر لعرض المشتريات',
                'S.No': 'رقم',
                'Purchases': 'مشتريات',
                'Shop': 'متجر',
                'Purchase Details': 'تفاصيل الشراء',
                'Items': 'العناصر',
                'Product': 'المنتج',
                'Quantity': 'الكمية',
                'Price': 'السعر',
                'Total': 'المجموع',
                'Subtotal': 'المجموع الفرعي',
                'Tax': 'الضريبة',
                'Discount': 'الخصم',
                'Update Purchase': 'تحديث الشراء',
                'Updating...': 'جاري التحديث...',
                'Purchase updated successfully': 'تم تحديث الشراء بنجاح',
                'Error updating purchase': 'خطأ في تحديث الشراء',
                'Confirm Delete': 'تأكيد الحذف',
                'Yes, Delete': 'نعم، حذف',
                'No, Cancel': 'لا، إلغاء',
                'Portrait': 'عمودي',
                'Landscape': 'أفقي',
                'A4': 'A4',
                'Letter': 'رسالة',
                'Legal': 'قانوني',
                'A5': 'A5',
                'Purchase Report': 'تقرير المشتريات',
                'Total Purchases': 'إجمالي المشتريات',
                'Generated': 'تم الإنشاء',
                'Report ID': 'معرف التقرير',
                'Generated by': 'تم الإنشاء بواسطة',
                'Shop Code': 'رمز المتجر',
                'FILTERS APPLIED': 'المرشحات المطبقة',
                'Search': 'بحث',
                'From': 'من',
                'To': 'إلى',
                'Page': 'صفحة',
                'Paid': 'مدفوع',
                'Unpaid': 'غير مدفوع',
                'Partially Paid': 'مدفوع جزئياً',
                'All': 'الكل',
                'Bill Number Filter': 'رقم الفاتورة',
                'Supplier Name Filter': 'اسم المورد',
                'Phone Filter': 'رقم الهاتف',
                'Added By Filter': 'أضيف بواسطة',
                'Date Range': 'نطاق التاريخ',
                'Start Date': 'تاريخ البدء',
                'End Date': 'تاريخ الانتهاء',
                'Payment Status Filter': 'حالة الدفع',
                'Filter By': 'تصفية حسب',
                'Orientation': 'الاتجاه',
                'Page Size': 'حجم الصفحة',
                'Cash': 'نقدي',
                'Card': 'بطاقة',
                'UPI': 'UPI',
                'Bank Transfer': 'تحويل بنكي',
                'cash': 'نقدي',
                'card': 'بطاقة',
                'upi': 'UPI',
                'bank_transfer': 'تحويل بنكي',
                'Sr#': 'رقم',
                'Batch': 'الدفعة',
                'Barcode': 'باركود',
                'Cost Price': 'سعر التكلفة',
                'Sell Price': 'سعر البيع',
                'Stock': 'المخزون',
                'Expiry': 'تاريخ الانتهاء',
                'Category': 'الفئة',
                'Purchase Receipt': 'إيصال الشراء',
                'Time': 'الوقت',
                'Supplier': 'المورد',
                'Address': 'العنوان',
                'Thank you for your business!': 'شكرا لك على عملك!',
                'Powered by OurZest': 'مدعوم من OurZest',
                'No data to export': 'لا توجد بيانات للتصدير',
                'Report generated!': 'تم إنشاء التقرير!',
                'pdfGenerationError': 'خطأ في إنشاء PDF',
                'readonlyNotice': 'لا يمكن تعديل رقم الفاتورة والعناصر بعد الإنشاء',
                'purchases': 'مشتريات',
                'pages': 'صفحات',
                'paymentStatus': 'حالة الدفع',
                'purchaseReceipt': 'إيصال الشراء',
                'receipt': 'إيصال',
                'notes': 'ملاحظات',
                'purchaseId': 'معرف الشراء',
                'itemBatch': 'الدفعة',
                'itemUnit': 'الوحدة'
            },
            'es': {
                'Purchase List': 'Lista de Compras',
                'All purchase details are below': 'Todos los detalles de compra están abajo',
                'Bill Number': 'Número de Factura',
                'Supplier Name': 'Nombre del Proveedor',
                'Phone': 'Teléfono',
                'Date': 'Fecha',
                'Grand Total': 'Total General',
                'Paid Amount': 'Monto Pagado',
                'Balance': 'Saldo',
                'Actions': 'Acciones',
                'Added By': 'Agregado Por',
                'Search purchases...': 'Buscar compras...',
                'Refresh': 'Actualizar',
                'Export PDF': 'Exportar PDF',
                'Edit': 'Editar',
                'Delete': 'Eliminar',
                'View': 'Ver',
                'Print': 'Imprimir',
                'Close': 'Cerrar',
                'Save Changes': 'Guardar Cambios',
                'Cancel': 'Cancelar',
                'Success': 'Éxito',
                'Error': 'Error',
                'Warning': 'Advertencia',
                'Showing': 'Mostrando',
                'of': 'de',
                'Previous': 'Anterior',
                'Next': 'Siguiente',
                'Switch Shop': 'Cambiar Tienda',
                'No shop selected': 'No se seleccionó ninguna tienda',
                'Please select a shop to view purchases': 'Por favor seleccione una tienda para ver las compras',
                'S.No': 'N°',
                'Purchases': 'Compras',
                'Shop': 'Tienda',
                'Purchase Details': 'Detalles de Compra',
                'Items': 'Elementos',
                'Product': 'Producto',
                'Quantity': 'Cantidad',
                'Price': 'Precio',
                'Total': 'Total',
                'Subtotal': 'Subtotal',
                'Tax': 'Impuesto',
                'Discount': 'Descuento',
                'Update Purchase': 'Actualizar Compra',
                'Updating...': 'Actualizando...',
                'Purchase updated successfully': 'Compra actualizada exitosamente',
                'Error updating purchase': 'Error al actualizar la compra',
                'Confirm Delete': 'Confirmar Eliminación',
                'Yes, Delete': 'Sí, Eliminar',
                'No, Cancel': 'No, Cancelar',
                'Portrait': 'Vertical',
                'Landscape': 'Horizontal',
                'A4': 'A4',
                'Letter': 'Carta',
                'Legal': 'Legal',
                'A5': 'A5',
                'Purchase Report': 'Reporte de Compras',
                'Total Purchases': 'Total de Compras',
                'Generated': 'Generado',
                'Report ID': 'ID del Reporte',
                'Generated by': 'Generado por',
                'Shop Code': 'Código de Tienda',
                'FILTERS APPLIED': 'FILTROS APLICADOS',
                'Search': 'Buscar',
                'From': 'Desde',
                'To': 'Hasta',
                'Page': 'Página',
                'Paid': 'Pagado',
                'Unpaid': 'No Pagado',
                'Partially Paid': 'Parcialmente Pagado',
                'All': 'Todos',
                'Bill Number Filter': 'Número de Factura',
                'Supplier Name Filter': 'Nombre del Proveedor',
                'Phone Filter': 'Número de Teléfono',
                'Added By Filter': 'Agregado Por',
                'Date Range': 'Rango de Fechas',
                'Start Date': 'Fecha Inicio',
                'End Date': 'Fecha Fin',
                'Payment Status Filter': 'Estado de Pago',
                'Filter By': 'Filtrar Por',
                'Orientation': 'Orientación',
                'Page Size': 'Tamaño de Página',
                'Cash': 'Efectivo',
                'Card': 'Tarjeta',
                'UPI': 'UPI',
                'Bank Transfer': 'Transferencia Bancaria',
                'cash': 'efectivo',
                'card': 'tarjeta',
                'upi': 'UPI',
                'bank_transfer': 'transferencia',
                'Sr#': 'N°',
                'Batch': 'Lote',
                'Barcode': 'Código de Barras',
                'Cost Price': 'Precio de Costo',
                'Sell Price': 'Precio de Venta',
                'Stock': 'Inventario',
                'Expiry': 'Vencimiento',
                'Category': 'Categoría',
                'Purchase Receipt': 'Recibo de Compra',
                'Time': 'Hora',
                'Supplier': 'Proveedor',
                'Address': 'Dirección',
                'Thank you for your business!': '¡Gracias por su negocio!',
                'Powered by OurZest': 'Desarrollado por OurZest',
                'No data to export': 'No hay datos para exportar',
                'Report generated!': '¡Informe generado!',
                'pdfGenerationError': 'Error al generar PDF',
                'readonlyNotice': 'El número de factura y los artículos no se pueden editar después de la creación',
                'purchases': 'compras',
                'pages': 'páginas',
                'paymentStatus': 'Estado de Pago',
                'purchaseReceipt': 'Recibo de Compra',
                'receipt': 'Recibo',
                'notes': 'Notas',
                'purchaseId': 'ID de Compra',
                'itemBatch': 'Lote',
                'itemUnit': 'Unidad'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });
            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) { }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) { }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;
        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }
        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();
        return translated;
    };

    const defaultTexts = {
        purchaseList: 'Purchase List',
        formDetails: 'All purchase details are below',
        purchases: 'Purchases',
        shopName: 'Shop',
        noShopSelected: 'No shop selected. Please select a shop first.',
        switchShop: 'Switch Shop',
        loadingShops: 'Loading shops...',
        noShops: 'No shops available',
        current: 'Current',
        searchPlaceholder: 'Search purchases...',
        refresh: 'Refresh',
        loadingUserData: 'Loading your purchase data...',
        selectShop: 'Please select a shop to view purchases',
        billNumber: 'Bill Number',
        supplierName: 'Supplier Name',
        phone: 'Phone',
        date: 'Date',
        grandTotal: 'Grand Total',
        paidAmount: 'Paid Amount',
        balance: 'Balance',
        actions: 'Actions',
        addedBy: 'Added By',
        edit: 'Edit',
        delete: 'Delete',
        view: 'View',
        print: 'Print',
        serialNo: 'S.No',
        showing: 'Showing',
        of: 'of',
        previous: 'Previous',
        next: 'Next',
        all: 'All',
        billNumberFilter: 'Bill Number',
        supplierNameFilter: 'Supplier Name',
        phoneFilter: 'Phone Number',
        addedByFilter: 'Added By',
        dateRange: 'Date Range',
        startDate: 'Start Date',
        endDate: 'End Date',
        paymentStatusFilter: 'Payment Status',
        filterBy: 'Filter By',
        exportToPDF: 'Export PDF',
        orientation: 'Orientation',
        portrait: 'Portrait',
        landscape: 'Landscape',
        pageSize: 'Page Size',
        a4: 'A4',
        letter: 'Letter',
        legal: 'Legal',
        a5: 'A5',
        purchaseDetails: 'Purchase Details',
        editPurchase: 'Edit Purchase',
        saveChanges: 'Save Changes',
        cancel: 'Cancel',
        confirmDelete: 'Confirm Delete',
        deleteMessage: 'Are you sure you want to delete this purchase?',
        deleteWarning: 'This action cannot be undone.',
        yesDelete: 'Yes, Delete',
        noCancel: 'No, Cancel',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        close: 'Close',
        purchaseReport: 'Purchase Report',
        purchaseDirectory: 'Purchase Directory',
        totalPurchases: 'Total Purchases',
        generated: 'Generated',
        reportId: 'Report ID',
        generatedBy: 'Generated by',
        shopCode: 'Shop Code',
        filtersApplied: 'FILTERS APPLIED',
        search: 'Search',
        from: 'From',
        to: 'To',
        page: 'Page',
        paid: 'Paid',
        unpaid: 'Unpaid',
        partiallyPaid: 'Partially Paid',
        items: 'Items',
        productName: 'Product',
        quantity: 'Qty',
        price: 'Price',
        total: 'Total',
        subtotal: 'Subtotal',
        tax: 'Tax',
        discount: 'Discount',
        updatePurchase: 'Update Purchase',
        updating: 'Updating...',
        purchaseUpdated: 'Purchase updated successfully',
        updateError: 'Error updating purchase',
        cash: 'Cash',
        card: 'Card',
        upi: 'UPI',
        bankTransfer: 'Bank Transfer',
        supplierAddress: 'Address',
        notes: 'Notes',
        purchaseId: 'Purchase ID',
        itemBatch: 'Batch',
        itemUnit: 'Unit',
        srNo: 'Sr#',
        batch: 'Batch',
        barcode: 'Barcode',
        costPrice: 'Cost Price',
        sellPrice: 'Sell Price',
        stock: 'Stock',
        expiry: 'Expiry',
        category: 'Category',
        purchaseReceipt: 'Purchase Receipt',
        time: 'Time',
        supplier: 'Supplier',
        address: 'Address',
        thankYou: 'Thank you for your business!',
        poweredBy: 'Powered by OurZest',
        noDataToExport: 'No data to export',
        reportGenerated: 'Report generated!',
        pdfGenerationError: 'Error generating PDF',
        readonlyNotice: 'items cannot be edited after creation',
        paymentStatus: 'Payment Status',
        receipt: 'Receipt'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    loadFromCache();

    return {
        getTranslations,
        translateText
    };
};

const purchasesListTranslator = createPurchasesListTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations.success || 'Success';
            case 'error': return translations.error || 'Error';
            case 'warning': return translations.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    return (
        <div className={`ozui_toast-notification ${toast.type}`}>
            <div className="ozui_toast-content">
                <div className="ozui_toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozui_toast-text">
                    <div className="ozui_toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozui_toast-message">{toast.message}</div>
                </div>
                <button className="ozui_toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// Shop Selector Modal Component
const ShopSelectorModal = ({ isOpen, onClose, shops, currentShop, onSelectShop, translations, loading, darkMode }) => {
    if (!isOpen) return null;

    return (
        <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={onClose}>
            <div className="ozlistui-modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="ozlistui-modal-header">
                    <h3>{translations.switchShop || 'Switch Shop'}</h3>
                    <button className="ozlistui-close-btn" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>
                <div className="ozlistui-modal-body">
                    {loading ? (
                        <div className="ozlistui-loading-shops">
                            <div className="ozlistui-loading-spinner-small"></div>
                            <p>{translations.loadingShops || 'Loading shops...'}</p>
                        </div>
                    ) : shops.length === 0 ? (
                        <div className="ozlistui-no-shops">
                            <p>{translations.noShops || 'No shops available'}</p>
                        </div>
                    ) : (
                        <div className="ozlistui-shop-list">
                            {shops.map((shop) => (
                                <div
                                    key={shop.shop_id}
                                    className={`ozlistui-shop-item ${currentShop?.shop_id === shop.shop_id ? 'ozlistui-active' : ''}`}
                                    onClick={() => onSelectShop(shop)}
                                >
                                    <div className="ozlistui-shop-item-icon">
                                        <FontAwesomeIcon icon={faStore} />
                                    </div>
                                    <div className="ozlistui-shop-item-details">
                                        <div className="ozlistui-shop-item-name">{shop.shop_name}</div>
                                        <div className="ozlistui-shop-item-code">{shop.shop_code || `ID: ${shop.shop_id}`}</div>
                                    </div>
                                    {currentShop?.shop_id === shop.shop_id && (
                                        <div className="ozlistui-shop-item-current">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                            <span>{translations.current || 'Current'}</span>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Custom Dropdown Component
const CustomDropdown = ({ label, icon, value, onChange, options, placeholder, translations, disabled = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [isHovering, setIsHovering] = useState(false);
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);
    const menuRef = useRef(null);
    const hoverTimeoutRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);
    const displayValue = selectedOption?.label || '';

    useEffect(() => {
        if (!isHovering) {
            setSearchTerm(displayValue);
        }
    }, [displayValue, isHovering]);

    useEffect(() => {
        if (isOpen) {
            const event = new CustomEvent('dropdownOpened', { detail: { id: label } });
            window.dispatchEvent(event);
        }
    }, [isOpen, label]);

    useEffect(() => {
        const handleDropdownOpened = (event) => {
            if (event.detail && event.detail.id !== label) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        window.addEventListener('dropdownOpened', handleDropdownOpened);
        return () => window.removeEventListener('dropdownOpened', handleDropdownOpened);
    }, [label]);

    const filteredOptions = isHovering
        ? options
        : searchTerm.trim() === ''
            ? options
            : options.filter(opt =>
                opt.label.toLowerCase().includes(searchTerm.toLowerCase())
            );

    const handleSelect = (option) => {
        onChange(option.value);
        setSearchTerm(option.label);
        setIsOpen(false);
        setHighlightedIndex(-1);
        setIsHovering(false);
    };

    const handleKeyDown = (e) => {
        if (!isOpen) {
            if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsOpen(true);
                setHighlightedIndex(0);
                setIsHovering(false);
            }
            return;
        }

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredOptions.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
                    handleSelect(filteredOptions[highlightedIndex]);
                }
                break;
            case 'Escape':
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            case 'Tab':
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
                break;
            default:
                break;
        }
    };

    const handleMouseEnter = () => {
        if (disabled) return;
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        hoverTimeoutRef.current = setTimeout(() => {
            setIsHovering(true);
            setIsOpen(true);
            setHighlightedIndex(0);
        }, 200);
    };

    const handleMouseLeave = () => {
        if (hoverTimeoutRef.current) {
            clearTimeout(hoverTimeoutRef.current);
        }
        setTimeout(() => {
            if (!isOpen) {
                setIsHovering(false);
            }
        }, 300);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
                setIsHovering(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        if (highlightedIndex >= 0 && menuRef.current) {
            const items = menuRef.current.querySelectorAll('.ozlistui-custom-dropdown-option');
            if (items[highlightedIndex]) {
                items[highlightedIndex].scrollIntoView({ block: 'nearest' });
            }
        }
    }, [highlightedIndex]);

    const handleInputClick = () => {
        if (disabled) return;
        setIsHovering(false);
        setSearchTerm(displayValue);
        setIsOpen(true);
        setHighlightedIndex(0);
    };

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setIsHovering(false);
        setSearchTerm(newValue);
        setIsOpen(true);
        setHighlightedIndex(0);
        if (!options.find(opt => opt.label.toLowerCase() === newValue.toLowerCase())) {
            onChange('');
        }
    };

    return (
        <div
            className="ozlistui-custom-dropdown-wrapper"
            ref={wrapperRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="ozlistui-custom-input-wrapper">
                <input
                    ref={inputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onClick={handleInputClick}
                    onFocus={() => {
                        if (!disabled) {
                            setIsHovering(false);
                            setSearchTerm(displayValue);
                            setIsOpen(true);
                            setHighlightedIndex(0);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    placeholder=" "
                    disabled={disabled}
                    autoComplete="off"
                />
                <label>
                    {icon && <FontAwesomeIcon icon={icon} />}
                    {label}
                </label>
                <FontAwesomeIcon icon={faChevronDown} className="ozlistui-custom-dropdown-arrow" />
            </div>

            {isOpen && (
                <div className="ozlistui-custom-dropdown-menu" ref={menuRef}>
                    {filteredOptions.length > 0 ? (
                        filteredOptions.map((opt, index) => (
                            <div
                                key={opt.value}
                                className={`ozlistui-custom-dropdown-option ${value === opt.value ? 'ozlistui-selected' : ''} ${highlightedIndex === index ? 'ozlistui-highlighted' : ''}`}
                                onClick={() => handleSelect(opt)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                            >
                                {opt.label}
                            </div>
                        ))
                    ) : (
                        <div className="ozlistui-custom-dropdown-empty">
                            No options found
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Purchase Items Table Component - FIXED with editable cost, profit %, and sell price
const PurchaseItemsTable = ({ 
    items, 
    onItemChange, 
    onRemove, 
    onAdd, 
    errors, 
    disabled, 
    translations, 
    isRTL,
    user,
    shopId,
    showToast 
}) => {
    // Handle cost price change - auto-calculate sell price based on profit %
    const handleCostChange = useCallback((index, value, item) => {
        const cost = parseFloat(value) || 0;
        const profitPercent = parseFloat(item.profit_percentage) || 0;
        let sellPrice = '';
        if (cost > 0 && profitPercent > 0) {
            sellPrice = (cost + (cost * profitPercent / 100)).toFixed(2);
        }
        onItemChange(index, 'cost_per_pack', value);
        if (sellPrice) {
            onItemChange(index, 'sell_per_pack', sellPrice);
        }
    }, [onItemChange]);

    // Handle profit percentage change - auto-calculate sell price
    const handleProfitChange = useCallback((index, value, item) => {
        const profitPercent = parseFloat(value) || 0;
        const cost = parseFloat(item.cost_per_pack) || 0;
        let sellPrice = '';
        if (cost > 0 && profitPercent > 0) {
            sellPrice = (cost + (cost * profitPercent / 100)).toFixed(2);
        }
        onItemChange(index, 'profit_percentage', value);
        if (sellPrice) {
            onItemChange(index, 'sell_per_pack', sellPrice);
        }
    }, [onItemChange]);

    return (
        <div className="ozlistui-items-table-container">
            <div className="ozlistui-items-table-wrapper">
                <table className="ozlistui-items-table">
                    <thead>
                        <tr>
                            <th style={{ width: '5%' }}>#</th>
                            <th style={{ width: '20%' }}>{translations.productName || 'Product'}</th>
                            <th style={{ width: '12%' }}>{translations.batch || 'Batch'}</th>
                            <th style={{ width: '10%' }}>{translations.quantity || 'Qty'}</th>
                            <th style={{ width: '15%' }}>{translations.costPrice || 'Cost Price'}</th>
                            <th style={{ width: '12%' }}>Profit %</th>
                            <th style={{ width: '15%' }}>{translations.sellPrice || 'Sell Price'}</th>
                            <th style={{ width: '12%' }}>{translations.expiry || 'Expiry'}</th>
                            <th style={{ width: '8%' }}>{translations.actions || 'Actions'}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => (
                            <tr key={index} className={errors?.items ? 'ozlistui-row-error' : ''}>
                                <td className="ozlistui-item-sr">{index + 1}</td>
                                <td>
                                    <input
                                        type="text"
                                        value={item.product_name || ''}
                                        onChange={(e) => onItemChange(index, 'product_name', e.target.value)}
                                        placeholder="Product name"
                                        disabled={disabled}
                                        className="ozlistui-item-input"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="text"
                                        value={item.batch_no || ''}
                                        onChange={(e) => onItemChange(index, 'batch_no', e.target.value)}
                                        placeholder="Batch no"
                                        disabled={disabled}
                                        className="ozlistui-item-input"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        value={item.quantity || ''}
                                        onChange={(e) => onItemChange(index, 'quantity', e.target.value)}
                                        placeholder="Qty"
                                        disabled={disabled}
                                        min="0"
                                        step="0.01"
                                        className="ozlistui-item-input"
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        value={item.cost_per_pack || ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            handleCostChange(index, val, item);
                                        }}
                                        placeholder="Cost"
                                        disabled={disabled}
                                        min="0"
                                        step="0.01"
                                        className="ozlistui-item-input ozlistui-cost-input"
                                        style={{ fontWeight: 'bold', color: '#059669' }}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        value={item.profit_percentage || ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            handleProfitChange(index, val, item);
                                        }}
                                        placeholder="%"
                                        disabled={disabled}
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        className="ozlistui-item-input ozlistui-profit-input"
                                        style={{ fontWeight: 'bold', color: '#2563eb' }}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="number"
                                        value={item.sell_per_pack || ''}
                                        onChange={(e) => onItemChange(index, 'sell_per_pack', e.target.value)}
                                        placeholder="Sell price"
                                        disabled={disabled}
                                        min="0"
                                        step="0.01"
                                        className="ozlistui-item-input ozlistui-sell-input"
                                        style={{ fontWeight: 'bold', color: '#7c3aed' }}
                                    />
                                </td>
                                <td>
                                    <input
                                        type="date"
                                        value={item.expiry_date || ''}
                                        onChange={(e) => onItemChange(index, 'expiry_date', e.target.value)}
                                        disabled={disabled}
                                        className="ozlistui-item-input"
                                        style={{ width: '120px' }}
                                    />
                                </td>
                                <td>
                                    <button
                                        type="button"
                                        onClick={() => onRemove(index)}
                                        disabled={disabled || items.length === 1}
                                        className="ozlistui-item-remove-btn"
                                        title="Remove item"
                                    >
                                        <FontAwesomeIcon icon={faTimes} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <button
                    type="button"
                    onClick={onAdd}
                    disabled={disabled}
                    className="ozlistui-item-add-btn"
                >
                    <FontAwesomeIcon icon={faPlus} />
                    {translations.addItem || 'Add Item'}
                </button>
            </div>
        </div>
    );
};

// Width modes for modal
const widthModes = {
    narrow: { width: 400, label: 'Narrow', icon: faMinus },
    medium: { width: 650, label: 'Medium', icon: faArrowsAlt },
    wide: { width: 850, label: 'Wide', icon: faPlus },
    full: { width: '95vw', label: 'Full Width', icon: faExpand },
    fullscreen: { width: '98vw', label: 'Full Screen', icon: faCompress }
};

// Main Component
const CosmeticsPurchaseList = () => {
    const { user, currentShop, switchShop, getShopsForSwitch } = useAuth();
    const [purchases, setPurchases] = useState([]);
    const [filteredPurchases, setFilteredPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPurchase, setSelectedPurchase] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [purchaseToDelete, setPurchaseToDelete] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [billLoading, setBillLoading] = useState(false);

    // PDF Options State
    const [pdfOrientation, setPdfOrientation] = useState('landscape');
    const [pdfPageSize, setPdfPageSize] = useState('a4');
    const [pdfPageRange, setPdfPageRange] = useState('all');
    const [pdfStartPage, setPdfStartPage] = useState(1);
    const [pdfEndPage, setPdfEndPage] = useState(1);

    // Shop selector state
    const [showShopSelector, setShowShopSelector] = useState(false);
    const [availableShops, setAvailableShops] = useState([]);
    const [loadingShops, setLoadingShops] = useState(false);

    // Modal drag and resize state
    const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
    const [modalSize, setModalSize] = useState({ width: 650, height: 'auto' });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [currentWidthMode, setCurrentWidthMode] = useState('wide');
    const [isModalInitialized, setIsModalInitialized] = useState(false);

    const dragStart = useRef({ x: 0, y: 0 });
    const modalRef = useRef(null);
    const resizeType = useRef(null);
    const resizeStart = useRef({ x: 0, y: 0, width: 0, height: 0, left: 0, top: 0 });

    // Theme, Dark Mode, Language State
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [currentTheme, setCurrentTheme] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    const [translations, setTranslations] = useState({});

    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        progress: 100,
        timeLeft: 3
    });

    const toastTimerRef = useRef(null);
    const isMountedRef = useRef(true);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        setToast({ show: true, type, message, progress: 100, timeLeft: 3 });
        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);
        toastTimerRef.current = timer;
    }, []);

    // Edit form state
    const [editFormData, setEditFormData] = useState({
        id: null,
        date: '',
        bill_no: '',
        supplierid: '',
        supplier_name: '',
        phone: '',
        address: '',
        grand_total: '',
        paid: '',
        balance: '',
        items: []
    });
    const [editFormErrors, setEditFormErrors] = useState({});
    const [editSuppliers, setEditSuppliers] = useState([]);
    const [editProductSearch, setEditProductSearch] = useState('');
    const [editProductResults, setEditProductResults] = useState([]);
    const [showEditProductResults, setShowEditProductResults] = useState(false);
    const [searchingEditProducts, setSearchingEditProducts] = useState(false);
    const editProductSearchRef = useRef(null);
    const editProductSearchTimerRef = useRef(null);
    const editProductSearchAbortRef = useRef(null);

    useEffect(() => {
        if (!user?.username || !currentShop?.shop_id) return;
        let cancelled = false;
        fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_suppliers.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'search_suppliers', searchTerm: '', username: user.username, shop_id: currentShop.shop_id })
        })
            .then(response => response.json())
            .then(result => { if (!cancelled && result.status === 'success') setEditSuppliers(result.data || []); })
            .catch(error => console.error('Could not load suppliers for purchase editing:', error));
        return () => { cancelled = true; };
    }, [user?.username, currentShop?.shop_id]);

    const calculateEditLineTotal = useCallback((item) => {
        const quantity = Number(item.quantity) || 0;
        const price = item.purchase_type === 'sub_packing'
            ? (Number(item.price) || 0)
            : (Number(item.cost_per_pack) || Number(item.price) || 0);
        return quantity * price;
    }, []);

    const recalculateEditPurchase = useCallback((items, paidValue) => {
        const grandTotal = items.reduce((sum, item) => sum + calculateEditLineTotal(item), 0);
        const paid = Number(paidValue) || 0;
        return {
            grand_total: grandTotal.toFixed(2),
            balance: (grandTotal - paid).toFixed(2)
        };
    }, [calculateEditLineTotal]);

    // FIXED: Update edit item with profit percentage support
    const updateEditItem = useCallback((index, field, value) => {
        setEditFormData(prev => {
            const items = prev.items.map((item, itemIndex) => {
                if (itemIndex !== index) return item;
                
                const updated = { ...item, [field]: value };
                
                // If cost_per_pack changes, auto-calculate sell price if profit percentage exists
                if (field === 'cost_per_pack') {
                    const cost = parseFloat(value) || 0;
                    const profitPercent = parseFloat(item.profit_percentage) || 0;
                    if (cost > 0 && profitPercent > 0) {
                        updated.sell_per_pack = (cost + (cost * profitPercent / 100)).toFixed(2);
                    }
                }
                
                // If profit_percentage changes, auto-calculate sell price
                if (field === 'profit_percentage') {
                    const cost = parseFloat(item.cost_per_pack) || 0;
                    const profitPercent = parseFloat(value) || 0;
                    if (cost > 0 && profitPercent > 0) {
                        updated.sell_per_pack = (cost + (cost * profitPercent / 100)).toFixed(2);
                    } else {
                        updated.sell_per_pack = '';
                    }
                }
                
                if (field === 'purchase_type') {
                    updated.is_sub_packing_purchase = value === 'sub_packing';
                    if (value === 'sub_packing' && !updated.price && updated.cost_per_pack) {
                        updated.price = String((Number(updated.cost_per_pack) || 0) / (Number(updated.items_per_pack) || 1));
                    }
                }
                return updated;
            });
            return { ...prev, items, ...recalculateEditPurchase(items, prev.paid) };
        });
        setEditFormErrors(prev => ({ ...prev, items: undefined }));
    }, [recalculateEditPurchase]);

    const addEditItem = useCallback(() => {
        setEditFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                product_id: '', product_name: '', product_code: '', barcode: '', batch_no: '',
                purchase_type: 'packing', packing_type: '', sub_packing_type: '',
                items_per_pack: '1', unit_of_measure: 'piece', price: '', cost_per_pack: '',
                sell_per_pack: '', profit_percentage: '', quantity: '1', expiry_date: ''
            }]
        }));
    }, []);

    const removeEditItem = useCallback((index) => {
        setEditFormData(prev => {
            const items = prev.items.filter((_, itemIndex) => itemIndex !== index);
            return { ...prev, items, ...recalculateEditPurchase(items, prev.paid) };
        });
    }, [recalculateEditPurchase]);

    // FIXED: Normalize product with profit percentage
    const normalizeProductForEdit = useCallback((product) => {
        const itemsPerPack = Number(product.items_per_pack) || 1;
        const costPerPack = Number(product.cost_per_pack) ||
            ((Number(product.cost_per_unit) || Number(product.cost_price) || 0) * itemsPerPack);
        const costPerUnit = Number(product.cost_per_unit) || Number(product.cost_price) ||
            (costPerPack / itemsPerPack);
        const purchaseType = product.purchase_type || 'packing';
        const sellPerPack = product.sell_per_pack || 
            (product.sell_price_per_unit ? Number(product.sell_price_per_unit) * itemsPerPack : '');
        const profitPercent = (costPerPack > 0 && sellPerPack) 
            ? (((parseFloat(sellPerPack) - costPerPack) / costPerPack) * 100).toFixed(1)
            : '';
        
        return {
            product_id: product.id || product.product_id || '',
            product_name: product.product_name || '',
            product_code: product.product_code || '',
            barcode: product.barcode || '',
            batch_no: product.batch_no || '',
            category: product.category || '', company: product.company || '',
            color: product.color || '', size: product.size || '',
            purchase_type: purchaseType,
            is_sub_packing_purchase: purchaseType === 'sub_packing',
            packing_type: product.packing_type || '',
            sub_packing_type: product.sub_packing_type || '',
            items_per_pack: String(itemsPerPack),
            unit_of_measure: product.unit_of_measure || 'piece',
            price: String(purchaseType === 'sub_packing' ? costPerUnit : costPerPack),
            cost_per_pack: String(costPerPack || ''),
            sell_per_pack: String(sellPerPack || ''),
            profit_percentage: String(profitPercent || ''),
            sell_price_per_unit: String(product.sell_price_per_unit || product.sell_price || ''),
            quantity: '1', expiry_date: product.expiry_date ? String(product.expiry_date).split('T')[0] : '',
            _pack_price: String(costPerPack || ''), _unit_price: String(costPerUnit || ''),
            available_stock_packs: Number(product.quantity) || 0,
            bquantity: product.bquantity || ''
        };
    }, []);

    const addProductToEdit = useCallback((product) => {
        const normalized = normalizeProductForEdit(product);
        setEditFormData(prev => {
            const existingIndex = prev.items.findIndex(item =>
                normalized.product_id && String(item.product_id) === String(normalized.product_id)
            );
            let items;
            if (existingIndex >= 0) {
                items = prev.items.map((item, index) => index === existingIndex
                    ? { ...item, quantity: String((Number(item.quantity) || 0) + 1) }
                    : item);
            } else {
                items = [...prev.items, normalized];
            }
            return { ...prev, items, ...recalculateEditPurchase(items, prev.paid) };
        });
        setEditProductSearch('');
        setShowEditProductResults(false);
        showToast('success', `${normalized.product_name} added`);
    }, [normalizeProductForEdit, recalculateEditPurchase, showToast]);

    const searchProductsForEdit = useCallback((term) => {
        setEditProductSearch(term);
        clearTimeout(editProductSearchTimerRef.current);
        editProductSearchAbortRef.current?.abort();
        if (term.trim().length < 2 || !user?.username || !currentShop?.shop_id) {
            setSearchingEditProducts(false);
            setEditProductResults([]); setShowEditProductResults(false); return;
        }
        setSearchingEditProducts(true);
        editProductSearchTimerRef.current = setTimeout(async () => {
            const controller = new AbortController();
            editProductSearchAbortRef.current = controller;
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' }, signal: controller.signal,
                    body: JSON.stringify({ action: 'search_products', searchTerm: term.trim(), username: user.username, shop_id: currentShop.shop_id })
                });
                const result = await response.json();
                const products = result.status === 'success' ? (result.data || []) : [];
                setEditProductResults(products);
                setShowEditProductResults(products.length > 0);
            } catch (error) {
                if (error.name !== 'AbortError') {
                    setEditProductResults([]);
                    showToast('error', 'Could not search products');
                }
            } finally {
                if (!controller.signal.aborted) setSearchingEditProducts(false);
            }
        }, 180);
    }, [user?.username, currentShop?.shop_id, showToast]);

    useEffect(() => () => {
        clearTimeout(editProductSearchTimerRef.current);
        editProductSearchAbortRef.current?.abort();
    }, []);

    const handleEditBarcodeScan = useCallback(async (barcode) => {
        if (!showEditModal || !barcode || !user?.username || !currentShop?.shop_id) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/cosmetics_module/cosmetics_products.php', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record_by_barcode', barcode, username: user.username, shop_id: currentShop.shop_id })
            });
            const result = await response.json();
            if (result.status === 'success' && result.data) addProductToEdit(result.data);
            else showToast('warning', `Product not found for barcode: ${barcode}`);
        } catch (error) { showToast('error', 'Could not process barcode'); }
    }, [showEditModal, user?.username, currentShop?.shop_id, addProductToEdit, showToast]);

    // Barcode scanner hook - assuming it exists
    const useBarcodeScanner = (callback, enabled) => {
        useEffect(() => {
            if (!enabled) return;
            const handleKeyPress = (e) => {
                if (e.key === 'Enter' && e.target.tagName !== 'INPUT' && e.target.tagName !== 'SELECT' && e.target.tagName !== 'TEXTAREA') {
                    // This would need proper barcode scanning logic
                }
            };
            document.addEventListener('keydown', handleKeyPress);
            return () => document.removeEventListener('keydown', handleKeyPress);
        }, [enabled, callback]);
    };

    useBarcodeScanner(handleEditBarcodeScan, showEditModal && !editLoading);

    // Business info state
    const [businessInfo, setBusinessInfo] = useState({
        businessname: '',
        bussinesslogo: '',
        bussinesslogo_url: '',
        businessaddress: '',
        fullname: '',
        email: '',
        phonenumber: '',
        gst_number: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(100);

    // Filter states
    const [filterBy, setFilterBy] = useState('');
    const [billNumberFilter, setBillNumberFilter] = useState('');
    const [supplierNameFilter, setSupplierNameFilter] = useState('');
    const [phoneFilter, setPhoneFilter] = useState('');
    const [addedByFilter, setAddedByFilter] = useState('');
    const [paymentStatusFilter, setPaymentStatusFilter] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const paymentStatusOptions = useMemo(() => [
        { value: '', label: translations.all || 'All' },
        { value: 'paid', label: translations.paid || 'Paid' },
        { value: 'unpaid', label: translations.unpaid || 'Unpaid' },
        { value: 'partial', label: translations.partiallyPaid || 'Partially Paid' }
    ], [translations]);

    const filterByOptions = useMemo(() => [
        { value: 'all', label: translations.all || 'All' },
        { value: 'billNumber', label: translations.billNumberFilter || 'Bill Number' },
        { value: 'supplierName', label: translations.supplierNameFilter || 'Supplier Name' },
        { value: 'phone', label: translations.phoneFilter || 'Phone Number' },
        { value: 'added_by', label: translations.addedByFilter || 'Added By' },
        { value: 'date', label: translations.dateRange || 'Date Range' },
        { value: 'paymentStatus', label: translations.paymentStatusFilter || 'Payment Status' }
    ], [translations]);

    const pdfOrientationOptions = useMemo(() => [
        { value: 'portrait', label: translations.portrait || 'Portrait' },
        { value: 'landscape', label: translations.landscape || 'Landscape' }
    ], [translations]);

    const pdfPageSizeOptions = useMemo(() => [
        { value: 'a4', label: translations.a4 || 'A4' },
        { value: 'letter', label: translations.letter || 'Letter' },
        { value: 'legal', label: translations.legal || 'Legal' },
        { value: 'a5', label: translations.a5 || 'A5' }
    ], [translations]);

    const pdfPageRangeOptions = useMemo(() => [
        { value: 'all', label: 'All Pages' },
        { value: 'range', label: 'Page Range' }
    ], []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        };
    }, []);

    // Theme sync
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        document.documentElement.style.setProperty('--ozlistui-primary', color);
        document.documentElement.style.setProperty('--ozlistui-primary-dark', color);
        document.documentElement.style.setProperty('--ozlistui-primary-light', color);
        setCurrentTheme(color);

        let style = document.getElementById('dynamic-list-styles-purchase');
        if (!style) {
            style = document.createElement('style');
            style.id = 'dynamic-list-styles-purchase';
            document.head.appendChild(style);
        }

        style.textContent = `
            .ozlistui-table thead th {
                color: ${color};
                border-bottom-color: ${color};
            }
            .ozlistui-payment-badge {
                border-color: ${color}40;
            }
            .ozlistui-page-number.ozlistui-active {
                background: ${color};
                border-color: ${color};
            }
            .ozlistui-action-btn.ozlistui-edit-btn:hover:not(:disabled) {
                background: ${color};
            }
            .ozlistui-header-shop-btn,
            .ozlistui-header-filter-btn {
                background: linear-gradient(135deg, ${color}, ${adjustColor(color, -20)});
            }
            .ozlistui-btn-primary {
                background: ${color};
            }
            .ozlistui-custom-dropdown-option.ozlistui-selected {
                background: ${color};
            }
        `;
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                const newTheme = e.newValue || '#6366f1';
                applyTheme(newTheme);
            }
        };

        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);

        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== currentTheme) {
                applyTheme(storedTheme);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyTheme, currentTheme]);

    // Dark mode sync
    const syncDarkMode = useCallback(() => {
        const isDark = localStorage.getItem('ourzest_darkMode') === 'true';
        setDarkMode(isDark);

        const container = document.querySelector('.ozlistui-container');
        if (container) {
            if (isDark) {
                container.classList.add('ozlistui-dark');
            } else {
                container.classList.remove('ozlistui-dark');
            }
        }

        const overlays = document.querySelectorAll('.ozlistui-modal-overlay');
        overlays.forEach(overlay => {
            if (isDark) {
                overlay.classList.add('ozlistui-dark');
            } else {
                overlay.classList.remove('ozlistui-dark');
            }
        });

        if (isDark) {
            document.body.classList.add('ozlistui-dark');
            document.documentElement.classList.add('ozlistui-dark');
        } else {
            document.body.classList.remove('ozlistui-dark');
            document.documentElement.classList.remove('ozlistui-dark');
        }
    }, []);

    useEffect(() => {
        syncDarkMode();

        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };

        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozlistui-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozlistui-dark');
                    } else {
                        container.classList.remove('ozlistui-dark');
                    }
                }
                const overlays = document.querySelectorAll('.ozlistui-modal-overlay');
                overlays.forEach(overlay => {
                    if (isDark) {
                        overlay.classList.add('ozlistui-dark');
                    } else {
                        overlay.classList.remove('ozlistui-dark');
                    }
                });
                if (isDark) {
                    document.body.classList.add('ozlistui-dark');
                    document.documentElement.classList.add('ozlistui-dark');
                } else {
                    document.body.classList.remove('ozlistui-dark');
                    document.documentElement.classList.remove('ozlistui-dark');
                }
            } else {
                syncDarkMode();
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);

        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [syncDarkMode, darkMode]);

    // Language sync
    const loadTranslations = useCallback(async (lang) => {
        const trans = await purchasesListTranslator.getTranslations(lang);
        setTranslations(trans);
    }, []);

    useEffect(() => {
        loadTranslations(currentLanguage);

        const handleStorageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };

        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };

        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);

        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage, loadTranslations]);

    // RTL direction sync
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozlistui-container');

        if (container) {
            if (isRTL) {
                container.classList.add('ozlistui-rtl');
                container.classList.remove('ozlistui-ltr');
            } else {
                container.classList.add('ozlistui-ltr');
                container.classList.remove('ozlistui-rtl');
            }
        }

        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // Center modal function
    const centerModal = useCallback(() => {
        if (!modalRef.current) return;
        const rect = modalRef.current.getBoundingClientRect();
        const left = (window.innerWidth - rect.width) / 2;
        const top = (window.innerHeight - rect.height) / 2;
        modalRef.current.style.left = `${Math.max(0, left)}px`;
        modalRef.current.style.top = `${Math.max(0, top)}px`;
        modalRef.current.style.transform = 'none';
        setModalPosition({ x: Math.max(0, left), y: Math.max(0, top) });
    }, []);

    const setWidthMode = useCallback((mode) => {
        if (!modalRef.current) return;
        setCurrentWidthMode(mode);
        const widthValue = widthModes[mode].width;
        if (mode === 'fullscreen') {
            modalRef.current.style.width = '98vw';
            modalRef.current.style.height = '90vh';
        } else {
            modalRef.current.style.width = typeof widthValue === 'number' ? `${widthValue}px` : widthValue;
            modalRef.current.style.height = 'auto';
        }
        setModalSize({ width: modalRef.current.offsetWidth, height: modalRef.current.offsetHeight });
        setTimeout(() => centerModal(), 10);
    }, [widthModes, centerModal]);

    const startDrag = useCallback((e) => {
        if (!modalRef.current) return;
        if (e.target.closest('button, input, select, textarea, a, .ozlistui-width-btn, .ozlistui-close-btn')) return;
        e.preventDefault();
        setIsDragging(true);
        const rect = modalRef.current.getBoundingClientRect();
        dragStart.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        modalRef.current.style.transition = 'none';
    }, []);

    const startResize = useCallback((e, type) => {
        e.preventDefault();
        e.stopPropagation();
        if (!modalRef.current) return;
        setIsResizing(true);
        resizeType.current = type;
        const rect = modalRef.current.getBoundingClientRect();
        resizeStart.current = {
            x: e.clientX, y: e.clientY,
            width: rect.width, height: rect.height,
            left: rect.left, top: rect.top,
            minWidth: 350, minHeight: 400,
            maxWidth: window.innerWidth - 40, maxHeight: window.innerHeight - 40
        };
        modalRef.current.style.transition = 'none';
    }, []);

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDragging && modalRef.current) {
                let newLeft = e.clientX - dragStart.current.x;
                let newTop = e.clientY - dragStart.current.y;
                const rect = modalRef.current.getBoundingClientRect();
                newLeft = Math.max(0, Math.min(newLeft, window.innerWidth - rect.width));
                newTop = Math.max(0, Math.min(newTop, window.innerHeight - rect.height));
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                modalRef.current.style.transform = 'none';
                setModalPosition({ x: newLeft, y: newTop });
            }

            if (isResizing && modalRef.current && resizeType.current) {
                let newWidth = resizeStart.current.width;
                let newHeight = resizeStart.current.height;
                let newLeft = resizeStart.current.left;
                let newTop = resizeStart.current.top;
                const deltaX = e.clientX - resizeStart.current.x;
                const deltaY = e.clientY - resizeStart.current.y;

                switch (resizeType.current) {
                    case 'e': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); break;
                    case 'w': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                    case 's': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'n': newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'ne': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'nw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height - deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); newTop = resizeStart.current.top + (resizeStart.current.height - newHeight); break;
                    case 'se': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width + deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); break;
                    case 'sw': newWidth = Math.min(resizeStart.current.maxWidth, Math.max(resizeStart.current.minWidth, resizeStart.current.width - deltaX)); newHeight = Math.min(resizeStart.current.maxHeight, Math.max(resizeStart.current.minHeight, resizeStart.current.height + deltaY)); newLeft = resizeStart.current.left + (resizeStart.current.width - newWidth); break;
                }
                modalRef.current.style.width = `${newWidth}px`;
                modalRef.current.style.height = `${newHeight}px`;
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalSize({ width: newWidth, height: newHeight });
                setModalPosition({ x: newLeft, y: newTop });
            }
        };

        const handleMouseUp = () => {
            if (isDragging || isResizing) {
                setIsDragging(false);
                setIsResizing(false);
                resizeType.current = null;
                if (modalRef.current) modalRef.current.style.transition = '';
            }
        };

        if (isDragging || isResizing) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, isResizing]);

    useEffect(() => {
        if (showEditModal && modalRef.current && !isModalInitialized) {
            setTimeout(() => { centerModal(); setWidthMode('wide'); setIsModalInitialized(true); }, 50);
        }
        if (!showEditModal) setIsModalInitialized(false);
    }, [showEditModal, centerModal, setWidthMode, isModalInitialized]);

    useEffect(() => {
        const handleWindowResize = () => {
            if (!modalRef.current || !showEditModal) return;
            const rect = modalRef.current.getBoundingClientRect();
            let newLeft = Math.min(rect.left, window.innerWidth - rect.width);
            let newTop = Math.min(rect.top, window.innerHeight - rect.height);
            newLeft = Math.max(0, newLeft);
            newTop = Math.max(0, newTop);
            if (newLeft !== rect.left || newTop !== rect.top) {
                modalRef.current.style.left = `${newLeft}px`;
                modalRef.current.style.top = `${newTop}px`;
                setModalPosition({ x: newLeft, y: newTop });
            }
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, [showEditModal]);

    // Fetch purchases
    const fetchPurchases = useCallback(async () => {
        if (!user || !user.username) {
            if (isMountedRef.current) {
                showToast('error', 'User information not available');
                setLoading(false);
            }
            return;
        }
        if (!currentShop) {
            if (isMountedRef.current) {
                showToast('error', translations.noShopSelected || 'Please select a shop first');
                setLoading(false);
            }
            return;
        }
        if (isMountedRef.current) setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_records',
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    filters: {}
                })
            });
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setPurchases(data.data || []);
                    setFilteredPurchases(data.data || []);
                    resetFilters();
                } else {
                    showToast('error', data.message || translations.networkError || 'Network error occurred');
                }
            }
        } catch (error) {
            console.error('Error fetching purchases:', error);
            if (isMountedRef.current) showToast('error', translations.networkError || 'Network error occurred');
        } finally {
            if (isMountedRef.current) setLoading(false);
        }
    }, [user, currentShop, translations, showToast]);

    const fetchAvailableShops = useCallback(async () => {
        if (!user) return;
        setLoadingShops(true);
        try {
            const shopsData = await getShopsForSwitch();
            if (isMountedRef.current) setAvailableShops(shopsData.shops || []);
        } catch (error) {
            console.error('Error loading shops:', error);
            if (isMountedRef.current) setAvailableShops([]);
        } finally {
            if (isMountedRef.current) setLoadingShops(false);
        }
    }, [user, getShopsForSwitch]);

    const fetchBusinessInfo = useCallback(async () => {
        if (!user || !user.username) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=get_business_info&username=' + encodeURIComponent(user.username), {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                credentials: 'include'
            });
            const data = await response.json();
            if (isMountedRef.current) {
                if (data.status === 'success') {
                    setBusinessInfo({
                        businessname: data.businessname || '',
                        bussinesslogo: data.bussinesslogo || '',
                        bussinesslogo_url: data.bussinesslogo_url || '',
                        businessaddress: data.businessaddress || '',
                        fullname: data.fullname || '',
                        email: data.email || '',
                        phonenumber: data.phonenumber || '',
                        gst_number: data.gst_number || ''
                    });
                } else {
                    setBusinessInfo({
                        businessname: user.business_name || user.fullname || '',
                        bussinesslogo: user.bussinesslogo || '',
                        bussinesslogo_url: user.bussinesslogo_url || '',
                        businessaddress: user.business_address || '',
                        fullname: user.fullname || '',
                        email: user.email || '',
                        phonenumber: user.phonenumber || '',
                        gst_number: user.gst_number || ''
                    });
                }
            }
        } catch (error) {
            console.error('Error fetching business info:', error);
            if (isMountedRef.current) {
                setBusinessInfo({
                    businessname: user.business_name || user.fullname || '',
                    bussinesslogo: user.bussinesslogo || '',
                    bussinesslogo_url: user.bussinesslogo_url || '',
                    businessaddress: user.business_address || '',
                    fullname: user.fullname || '',
                    email: user.email || '',
                    phonenumber: user.phonenumber || '',
                    gst_number: user.gst_number || ''
                });
            }
        }
    }, [user]);

    useEffect(() => {
        if (user && user.username && currentShop) {
            fetchPurchases();
            fetchBusinessInfo();
            fetchAvailableShops();
        } else if (user && user.username && !currentShop) {
            setLoading(false);
        } else if (!user) {
            setLoading(false);
        }
    }, [user, currentShop?.shop_id]);

    const handleSwitchShop = useCallback(async (shop) => {
        try {
            const success = await switchShop(shop.shop_id, shop.shop_name, shop.shop_code);
            if (success) {
                showToast('success', 'Shop switched successfully');
                setCurrentPage(1);
                resetFilters();
                setShowShopSelector(false);
                fetchPurchases();
            } else {
                showToast('error', 'Failed to switch shop');
            }
        } catch (error) {
            console.error('Error switching shop:', error);
            showToast('error', 'Failed to switch shop');
        }
    }, [switchShop, showToast, fetchPurchases]);

    const applyFilters = useCallback(() => {
        let filtered = [...purchases];
        if (searchTerm.trim()) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(purchase =>
                purchase.bill_no?.toLowerCase().includes(term) ||
                purchase.supplier_name?.toLowerCase().includes(term) ||
                purchase.phone?.toLowerCase().includes(term) ||
                purchase.username?.toLowerCase().includes(term)
            );
        }
        if (filterBy === 'billNumber' && billNumberFilter.trim()) {
            const term = billNumberFilter.toLowerCase();
            filtered = filtered.filter(purchase => purchase.bill_no?.toLowerCase().includes(term));
        }
        if (filterBy === 'supplierName' && supplierNameFilter.trim()) {
            const term = supplierNameFilter.toLowerCase();
            filtered = filtered.filter(purchase => purchase.supplier_name?.toLowerCase().includes(term));
        }
        if (filterBy === 'phone' && phoneFilter.trim()) {
            const term = phoneFilter.toLowerCase();
            filtered = filtered.filter(purchase => purchase.phone?.toLowerCase().includes(term));
        }
        if (filterBy === 'added_by' && addedByFilter.trim()) {
            const term = addedByFilter.toLowerCase();
            filtered = filtered.filter(purchase => purchase.username?.toLowerCase().includes(term));
        }
        if (filterBy === 'paymentStatus' && paymentStatusFilter) {
            if (paymentStatusFilter === 'paid') {
                filtered = filtered.filter(purchase => {
                    const balance = parseFloat(purchase.balance) || 0;
                    return balance <= 0;
                });
            } else if (paymentStatusFilter === 'unpaid') {
                filtered = filtered.filter(purchase => {
                    const paid = parseFloat(purchase.paid) || 0;
                    return paid === 0;
                });
            } else if (paymentStatusFilter === 'partial') {
                filtered = filtered.filter(purchase => {
                    const paid = parseFloat(purchase.paid) || 0;
                    const balance = parseFloat(purchase.balance) || 0;
                    return paid > 0 && balance > 0;
                });
            }
        }
        if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            filtered = filtered.filter(purchase => {
                if (!purchase.date) return false;
                const purchaseDate = new Date(purchase.date);
                purchaseDate.setHours(0, 0, 0, 0);
                return purchaseDate >= start;
            });
        }
        if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            filtered = filtered.filter(purchase => {
                if (!purchase.date) return false;
                const purchaseDate = new Date(purchase.date);
                return purchaseDate <= end;
            });
        }
        setFilteredPurchases(filtered);
        setCurrentPage(1);
    }, [purchases, searchTerm, filterBy, billNumberFilter, supplierNameFilter, phoneFilter, addedByFilter, paymentStatusFilter, startDate, endDate]);

    useEffect(() => { applyFilters(); }, [applyFilters]);

    const resetFilters = useCallback(() => {
        setFilterBy('');
        setBillNumberFilter('');
        setSupplierNameFilter('');
        setPhoneFilter('');
        setAddedByFilter('');
        setPaymentStatusFilter('');
        setStartDate('');
        setEndDate('');
        setSearchTerm('');
    }, []);

    const formatDate = useCallback((dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        } catch { return dateString; }
    }, []);

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return 'Rs. 0.00';
        return `Rs. ${parseFloat(amount).toFixed(2)}`;
    };

    const getPaymentStatus = (paid, grandTotal) => {
        const paidAmount = parseFloat(paid) || 0;
        const totalAmount = parseFloat(grandTotal) || 0;
        const balance = totalAmount - paidAmount;

        if (balance <= 0) {
            return { status: 'paid', text: translations.paid || 'Paid', color: '#10b981' };
        } else if (paidAmount === 0) {
            return { status: 'unpaid', text: translations.unpaid || 'Unpaid', color: '#ef4444' };
        } else {
            return { status: 'partial', text: translations.partiallyPaid || 'Partial', color: '#f59e0b' };
        }
    };

    const getTodayDate = useCallback(() => new Date().toISOString().split('T')[0], []);

    const getRowsPerPage = useCallback(() => {
        if (pdfOrientation === 'landscape') {
            return 20;
        }
        return 15;
    }, [pdfOrientation]);

    const pagination = useMemo(() => {
        const totalPages = Math.ceil(filteredPurchases.length / itemsPerPage);
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const currentPurchases = filteredPurchases.slice(startIndex, endIndex);
        return { totalPages, startIndex, endIndex, currentPurchases };
    }, [filteredPurchases, currentPage, itemsPerPage]);

    // PDF export function
    const exportToPDF = useCallback(async () => {
        if (!filteredPurchases || filteredPurchases.length === 0) {
            showToast('warning', translations.noDataToExport || 'No data to export');
            return;
        }
        setPdfLoading(true);
        try {
            const currentLang = currentLanguage;
            const isRTL = currentLang === 'ur' || currentLang === 'ar';

            const pdfTextColor = '#1f2937';
            const pdfSubTextColor = '#6b7280';
            const pdfBorderColor = '#e5e7eb';
            const pdfHeaderBgColor = '#f9fafb';
            const pdfRowEvenBg = '#ffffff';
            const pdfRowOddBg = '#f9fafb';
            const pdfThemeColor = currentTheme;

            const pageSizeMap = { 'a4': 'a4', 'letter': 'letter', 'legal': 'legal', 'a5': 'a5' };
            const pageFormat = pageSizeMap[pdfPageSize] || 'a4';
            const pdf = new jsPDF({ orientation: pdfOrientation, unit: 'mm', format: pageFormat, compress: true });
            const pageWidth = pdf.internal.pageSize.getWidth();
            const rowsPerPage = getRowsPerPage();

            let purchasesToExport = [...filteredPurchases];

            if (pdfPageRange === 'range') {
                const totalPagesForRange = Math.ceil(purchasesToExport.length / rowsPerPage);
                const start = Math.max(1, pdfStartPage);
                const end = Math.min(totalPagesForRange, pdfEndPage);
                const startIdx = (start - 1) * rowsPerPage;
                const endIdx = Math.min(end * rowsPerPage, purchasesToExport.length);
                purchasesToExport = purchasesToExport.slice(startIdx, endIdx);
            }

            const totalPages = Math.ceil(purchasesToExport.length / rowsPerPage);

            const getPDFTranslation = (key, fallback = '') => {
                return translations[key] || fallback;
            };

            const formatCurrencyForPDF = (amount) => {
                if (!amount && amount !== 0) return 'Rs. 0.00';
                return `Rs. ${parseFloat(amount).toFixed(2)}`;
            };

            const formatDateForPDF = (dateString) => {
                if (!dateString) return '-';
                try {
                    const date = new Date(dateString);
                    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                } catch { return dateString; }
            };

            const renderPage = async (pagePurchases, pageNum, startRowIndex) => {
                return new Promise((resolve, reject) => {
                    try {
                        const container = document.createElement('div');
                        container.style.width = '750px';
                        container.style.padding = '12px 15px';
                        container.style.backgroundColor = '#ffffff';
                        container.style.color = pdfTextColor;
                        container.style.position = 'absolute';
                        container.style.left = '-9999px';
                        container.style.top = '0';
                        container.style.fontFamily = "'Segoe UI', 'Helvetica Neue', Arial, sans-serif";
                        container.style.direction = isRTL ? 'rtl' : 'ltr';

                        const purchaseReportLabel = getPDFTranslation('purchaseReport', 'Purchase Report');
                        const purchaseDirectoryLabel = getPDFTranslation('purchaseDirectory', 'Purchase Directory');
                        const totalPurchasesLabel = getPDFTranslation('totalPurchases', 'Total Purchases');
                        const generatedLabel = getPDFTranslation('generated', 'Generated');
                        const reportIdLabel = getPDFTranslation('reportId', 'Report ID');
                        const generatedByLabel = getPDFTranslation('generatedBy', 'Generated by');
                        const shopCodeLabel = getPDFTranslation('shopCode', 'Shop Code');
                        const filtersAppliedLabel = getPDFTranslation('filtersApplied', 'FILTERS APPLIED');
                        const searchLabel = getPDFTranslation('search', 'Search');
                        const fromLabel = getPDFTranslation('from', 'From');
                        const toLabel = getPDFTranslation('to', 'To');
                        const pageLabel = getPDFTranslation('page', 'Page');
                        const ofLabel = getPDFTranslation('of', 'of');
                        const billNumberLabel = getPDFTranslation('billNumber', 'Bill No');
                        const supplierNameLabel = getPDFTranslation('supplierName', 'Supplier');
                        const phoneLabel = getPDFTranslation('phone', 'Phone');
                        const dateLabel = getPDFTranslation('date', 'Date');
                        const grandTotalLabel = getPDFTranslation('grandTotal', 'Total');
                        const paidAmountLabel = getPDFTranslation('paidAmount', 'Paid');
                        const balanceLabel = getPDFTranslation('balance', 'Balance');
                        const paymentStatusLabel = getPDFTranslation('paymentStatus', 'Status');
                        const addedByLabel = getPDFTranslation('addedBy', 'Added By');

                        const totalGrandTotal = pagePurchases.reduce((sum, purchase) => sum + (parseFloat(purchase.grand_total) || 0), 0);
                        const totalPaid = pagePurchases.reduce((sum, purchase) => sum + (parseFloat(purchase.paid) || 0), 0);
                        const totalBalance = pagePurchases.reduce((sum, purchase) => sum + (parseFloat(purchase.balance) || 0), 0);

                        const headerHTML = `
                            <div style="margin-bottom: 12px; text-align: center;">
                                <div style="font-size: 8px; letter-spacing: 3px; color: ${pdfSubTextColor}; margin-bottom: 4px; text-transform: uppercase; font-weight: 500;">${purchaseReportLabel}</div>
                                <h1 style="color: ${pdfTextColor}; font-size: 20px; margin: 0 0 4px 0; font-weight: 600;">
                                    ${escapeHtml(currentShop?.shop_name) || escapeHtml(purchaseDirectoryLabel)}
                                </h1>
                                <div style="width: 40px; height: 2px; background: ${pdfThemeColor}; margin: 6px auto;"></div>
                                ${businessInfo.businessname ? `<div style="color: ${pdfSubTextColor}; font-size: 10px; margin-top: 4px;">${escapeHtml(businessInfo.businessname)}</div>` : ''}
                                <div style="color: ${pdfSubTextColor}; font-size: 7px; margin-top: 4px;">
                                    ${totalPurchasesLabel}: ${purchasesToExport.length} | ${generatedLabel}: ${new Date().toLocaleString(currentLang)}
                                </div>
                            </div>
                            <div style="display: flex; justify-content: space-between; margin: 8px 0 10px 0; padding: 5px 0; border-top: 1px solid ${pdfBorderColor}; border-bottom: 1px solid ${pdfBorderColor}; ${isRTL ? 'flex-direction: row-reverse;' : ''}">
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${reportIdLabel}:</strong> #${new Date().toISOString().slice(0, 10).replace(/-/g, '')}-${String(pageNum).padStart(3, '0')}</div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${generatedByLabel}:</strong> ${escapeHtml(user?.fullname || user?.username || 'System')}</div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};"><strong>${shopCodeLabel}:</strong> ${escapeHtml(currentShop?.shop_code || 'N/A')}</div>
                            </div>
                        `;

                        const hasFilters = searchTerm || paymentStatusFilter || startDate || endDate;
                        const filterHTML = hasFilters ? `
                            <div style="margin: 6px 0; padding: 4px 10px; background: #f7f7f7; border-${isRTL ? 'right' : 'left'}: 3px solid ${pdfThemeColor}; font-size: 6.5px; color: ${pdfSubTextColor};">
                                <span style="font-weight: 600;">${filtersAppliedLabel}:</span> 
                                ${searchTerm ? `${searchLabel}: "${escapeHtml(searchTerm)}"` : ''}
                                ${paymentStatusFilter ? ` | ${getPDFTranslation('paymentStatus', 'Status')}: ${paymentStatusFilter === 'paid' ? getPDFTranslation('paid', 'Paid') : paymentStatusFilter === 'unpaid' ? getPDFTranslation('unpaid', 'Unpaid') : getPDFTranslation('partiallyPaid', 'Partial')}` : ''}
                                ${startDate ? ` | ${fromLabel}: ${startDate}` : ''}
                                ${endDate ? ` | ${toLabel}: ${endDate}` : ''}
                            </div>
                        ` : '';

                        let tableHTML = `
                            <table style="width: 100%; border-collapse: collapse; font-size: 6.5px; margin-top: 4px;">
                                <thead>
                                    <tr style="background-color: ${pdfHeaderBgColor}; border-bottom: 2px solid ${pdfThemeColor};">
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 4%;">#</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${dateLabel}</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 14%;">${billNumberLabel}</th>
                                        <th style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 18%;">${supplierNameLabel}</th>
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${phoneLabel}</th>
                                        <th style="padding: 4px 3px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${grandTotalLabel}</th>
                                        <th style="padding: 4px 3px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${paidAmountLabel}</th>
                                        <th style="padding: 4px 3px; text-align: right; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 10%;">${balanceLabel}</th>
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; border-right: 1px solid ${pdfBorderColor}; width: 8%;">${paymentStatusLabel}</th>
                                        <th style="padding: 4px 3px; text-align: center; font-weight: 700; color: ${pdfTextColor}; width: 6%;">${addedByLabel}</th>
                                    </tr>
                                </thead>
                                <tbody>
                        `;

                        pagePurchases.forEach((purchase, idx) => {
                            const rowNum = startRowIndex + idx + 1;
                            const isEven = idx % 2 === 0;
                            const paymentStatus = getPaymentStatus(purchase.paid, purchase.grand_total);

                            tableHTML += `
                                <tr style="border-bottom: 0.5px solid ${pdfBorderColor}; background: ${isEven ? pdfRowEvenBg : pdfRowOddBg};">
                                    <td style="padding: 3px; text-align: center; color: ${pdfSubTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${rowNum}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${formatDateForPDF(purchase.date)}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; font-weight: 500; border-right: 0.5px solid ${pdfBorderColor}; font-family: monospace;">${escapeHtml(purchase.bill_no) || '-'}</td>
                                    <td style="padding: 3px; text-align: ${isRTL ? 'right' : 'left'}; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor};">${escapeHtml(purchase.supplier_name) || '-'}</td>
                                    <td style="padding: 3px; text-align: center; color: ${pdfTextColor}; border-right: 0.5px solid ${pdfBorderColor}; font-family: monospace;">${escapeHtml(purchase.phone) || '—'}</td>
                                    <td style="padding: 3px; text-align: right; color: #059669; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor};">${formatCurrencyForPDF(purchase.grand_total)}</td>
                                    <td style="padding: 3px; text-align: right; color: #3b82f6; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor};">${formatCurrencyForPDF(purchase.paid)}</td>
                                    <td style="padding: 3px; text-align: right; color: ${parseFloat(purchase.balance) > 0 ? '#d97706' : '#059669'}; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor};">${formatCurrencyForPDF(purchase.balance)}</td>
                                    <td style="padding: 3px; text-align: center; color: ${paymentStatus.color}; font-weight: 600; border-right: 0.5px solid ${pdfBorderColor};">${paymentStatus.text}</td>
                                    <td style="padding: 3px; text-align: center; color: ${pdfTextColor};">${escapeHtml(purchase.username) || '—'}</td>
                                </tr>
                            `;
                        });

                        const emptyRowsNeeded = rowsPerPage - pagePurchases.length;
                        for (let i = 0; i < emptyRowsNeeded; i++) {
                            tableHTML += `
                                <tr style="border-bottom: 0.5px solid ${pdfBorderColor}; background: ${(pagePurchases.length + i) % 2 === 0 ? pdfRowEvenBg : pdfRowOddBg};">
                                    <td style="padding: 3px; text-align: center; color: ${pdfSubTextColor}; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px; border-right: 0.5px solid ${pdfBorderColor};">&nbsp;</td>
                                    <td style="padding: 3px;">&nbsp;</td>
                                </tr>
                            `;
                        }

                        tableHTML += `
                                    <tr style="border-top: 1px solid ${pdfBorderColor}; background-color: #f8f8f8;">
                                        <td colspan="4" style="padding: 4px 3px; text-align: ${isRTL ? 'right' : 'left'}; font-weight: 700;">Page Summary</td>
                                        <td style="padding: 4px 3px; text-align: right; font-weight: 700; color: #059669;">${formatCurrencyForPDF(totalGrandTotal)}</td>
                                        <td style="padding: 4px 3px; text-align: right; font-weight: 700; color: #3b82f6;">${formatCurrencyForPDF(totalPaid)}</td>
                                        <td style="padding: 4px 3px; text-align: right; font-weight: 700; color: #d97706;">${formatCurrencyForPDF(totalBalance)}</td>
                                        <td colspan="3">&nbsp;</td>
                                      </tr>
                                </tbody>
                              </table>
                        `;

                        const footerHTML = `
                            <div style="margin-top: 8px; padding-top: 5px; text-align: center;">
                                <div style="width: 30px; height: 1px; background: ${pdfBorderColor}; margin: 0 auto 5px auto;"></div>
                                <div style="font-size: 6.5px; color: ${pdfSubTextColor};">${pageLabel} ${pageNum} ${ofLabel} ${totalPages}</div>
                                <div style="font-size: 5px; color: #aaa; margin-top: 3px;">${translations.poweredBy || 'Powered by OurZest'}</div>
                            </div>
                        `;

                        container.innerHTML = headerHTML + filterHTML + tableHTML + footerHTML;
                        document.body.appendChild(container);
                        html2canvas(container, { scale: 2, backgroundColor: '#ffffff', logging: false }).then(canvas => {
                            document.body.removeChild(container);
                            resolve(canvas);
                        }).catch(err => { document.body.removeChild(container); reject(err); });
                    } catch (err) { reject(err); }
                });
            };

            for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
                const startRow = (pageNum - 1) * rowsPerPage;
                const endRow = Math.min(pageNum * rowsPerPage, purchasesToExport.length);
                const pagePurchases = purchasesToExport.slice(startRow, endRow);
                const canvas = await renderPage(pagePurchases, pageNum, startRow);
                if (pageNum > 1) {
                    pdf.addPage();
                }
                const imgWidth = pageWidth - 20;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;
                pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 10, 5, imgWidth, imgHeight);
            }

            const fileName = `Purchase_Report_${currentShop?.shop_name?.replace(/[^a-z0-9]/gi, '_') || 'Export'}_${new Date().toISOString().slice(0, 10)}.pdf`;
            pdf.save(fileName);
            showToast('success', `${translations.reportGenerated || 'Report generated!'} ${purchasesToExport.length} ${translations.purchases || 'purchases'} on ${totalPages} ${translations.pages || 'page(s)'}`);
        } catch (error) {
            console.error('PDF Error:', error);
            showToast('error', translations.pdfGenerationError || 'Error generating PDF');
        } finally {
            setPdfLoading(false);
        }
    }, [filteredPurchases, currentLanguage, pdfOrientation, pdfPageSize, pdfPageRange, pdfStartPage, pdfEndPage, translations, businessInfo, currentShop, user, showToast, getRowsPerPage, searchTerm, paymentStatusFilter, startDate, endDate, currentTheme]);

    // Print bill function
    const handlePrintBill = async (purchase) => {
        if (!purchase) return;

        showToast('info', 'Loading purchase details for print...');

        let purchaseData = purchase;

        if (!purchase.items && purchase.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: purchase.id,
                        shop_id: currentShop.shop_id
                    })
                });
                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    purchaseData = data.data;
                } else {
                    showToast('error', 'Could not load purchase details for printing');
                    return;
                }
            } catch (error) {
                console.error('Error fetching purchase details for print:', error);
                showToast('error', 'Could not load purchase details for printing');
                return;
            }
        }

        if (!purchaseData.items || purchaseData.items.length === 0) {
            showToast('warning', 'No items to print receipt');
            return;
        }

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            showToast('error', 'Please allow popups for printing');
            return;
        }

        const shopName = businessInfo?.businessname || currentShop?.shop_name || 'OurZest Store';
        const shopAddress = businessInfo?.businessaddress || '';
        const businessPhone = businessInfo?.phonenumber || '';
        const businessLogo = businessInfo?.bussinesslogo_url || businessInfo?.bussinesslogo || '';
        const supplierName = purchaseData.supplier_name || 'Walk-in Supplier';

        const formatReceiptDateTime = (dateString) => {
            const date = dateString ? new Date(dateString) : new Date();
            const validDate = isNaN(date.getTime()) ? new Date() : date;
            const datePart = validDate.toLocaleDateString('en-IN', {
                day: '2-digit', month: '2-digit', year: 'numeric'
            });
            const timePart = validDate.toLocaleTimeString('en-IN', {
                hour: '2-digit', minute: '2-digit', hour12: true
            });
            return `${datePart} ${timePart}`;
        };

        const formatReceiptCurrency = (amount) => {
            if (!amount && amount !== 0) return 'Rs. 0';
            return `Rs. ${parseFloat(amount).toFixed(0)}`;
        };

        const receiptDateTime = formatReceiptDateTime(purchaseData.date);
        const grandTotal = purchaseData.grand_total || '0.00';
        const paidAmount = purchaseData.paid || '0.00';
        const balance = purchaseData.balance || (parseFloat(grandTotal) - parseFloat(paidAmount)).toFixed(2);
        const tax = purchaseData.tax || 0;
        const discount = purchaseData.discount || 0;

        printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
            <title>Purchase Bill ${purchaseData.bill_no}</title>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <style>
                @page {
                    size: 80mm auto;
                    margin: 0;
                }
                * { 
                    margin: 0; 
                    padding: 0; 
                    box-sizing: border-box; 
                }
                body {
                    background: #ffffff;
                    font-family: 'Courier New', Courier, monospace;
                    font-weight: 700;
                    color: #000000;
                    font-size: 12px;
                    line-height: 1.3;
                    padding: 5px 4px;
                    max-width: 80mm;
                    margin: 0 auto;
                    transform: scale(0.90);
                    transform-origin: top center;
                    display: flex;
                    justify-content: center;
                    align-items: flex-start;
                    min-height: 100vh;
                }
                .receipt-wrapper {
                    width: 100%;
                    max-width: 80mm;
                    transform-origin: top center;
                }
                .receipt { 
                    width: 100%; 
                    padding: 2px 0;
                    margin: 0 auto;
                }
                .text-center { text-align: center; }
                .text-right { text-align: right; }
                .text-left { text-align: left; }
                .fw-900 { font-weight: 900; }
                .fw-700 { font-weight: 700; }
                .border-bottom { border-bottom: 3px double #000; padding-bottom: 4px; margin-bottom: 4px; }
                .border-top { border-top: 2px solid #000; padding-top: 4px; margin-top: 4px; }
                .border-dash { border-bottom: 1px dashed #000; padding-bottom: 3px; margin-bottom: 3px; }
                .flex-row { display: flex; justify-content: space-between; align-items: baseline; }
                .flex-col { display: flex; flex-direction: column; }
                .gap-1 { gap: 3px; }
                .mt-1 { margin-top: 3px; }
                .mb-1 { margin-bottom: 3px; }
                .p-1 { padding: 3px; }
                .bg-light { background: #f2f2f2; }
                .header-center {
                    text-align: center;
                    border-bottom: 3px double #000;
                    padding-bottom: 4px;
                    margin-bottom: 4px;
                }
                .header-logo {
                    display: block;
                    margin: 0 auto 2px auto;
                    max-height: 85px;
                    max-width: 120px;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                }
                .business-name {
                    font-size: 17px;
                    font-weight: 900;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }
                .business-phone {
                    font-size: 11px;
                    font-weight: 700;
                    margin-top: 1px;
                    color: #000;
                }
                .business-addr {
                    font-size: 10px;
                    font-weight: 700;
                    margin-top: 1px;
                }
                .receipt-title {
                    text-align: center;
                    font-size: 18px;
                    font-weight: 900;
                    letter-spacing: 2px;
                    border-bottom: 2px solid #000;
                    padding-bottom: 2px;
                    margin-bottom: 2px;
                }
                .receipt-bill-number {
                    text-align: center;
                    font-size: 12px;
                    font-weight: 700;
                    margin-bottom: 3px;
                }
                .receipt-bill-number strong { font-weight: 900; }
                .receipt-customer {
                    text-align: center;
                    font-size: 12px;
                    font-weight: 700;
                    margin-bottom: 4px;
                }
                .receipt-customer strong { font-weight: 900; }
                .bill-meta {
                    display: flex;
                    justify-content: space-between;
                    font-size: 12px;
                    font-weight: 700;
                    padding: 2px 0;
                    border-bottom: 1px dashed #000;
                    margin-bottom: 3px;
                }
                .customer-box {
                    background: #f5f5f5;
                    padding: 4px 6px;
                    border-left: 6px solid #000;
                    border: 1px solid #000;
                    border-left-width: 6px;
                    margin: 3px 0 6px 0;
                    font-size: 11px;
                }
                .items-table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 4px 0 6px 0;
                    border: 2px solid #000;
                }
                .items-table th {
                    background: #e0e0e0;
                    text-align: center;
                    padding: 3px 3px;
                    font-size: 9px;
                    text-transform: uppercase;
                    border: 1px solid #000;
                    font-weight: 900;
                }
                .items-table td {
                    padding: 3px 3px;
                    border: 1px solid #000;
                    font-size: 10px;
                    font-weight: 700;
                    vertical-align: middle;
                    text-align: center;
                }
                .items-table th:first-child, .items-table td:first-child { text-align: center; }
                .items-table th:nth-child(2), .items-table td:nth-child(2) { text-align: left; }
                .product-name { font-weight: 900; word-break: break-word; }
                .payment-box {
                    margin-top: 4px;
                    padding-top: 3px;
                    border-top: 2px solid #000;
                }
                .payment-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 1px 0;
                    font-size: 12px;
                    font-weight: 700;
                }
                .payment-row.total {
                    font-size: 14px;
                    font-weight: 900;
                    border-top: 2px solid #000;
                    margin-top: 3px;
                    padding-top: 4px;
                }
                .footer {
                    text-align: center;
                    font-size: 10px;
                    margin-top: 6px;
                    padding-top: 4px;
                    border-top: 2px solid #000;
                    font-weight: 700;
                }
                .thankyou { font-size: 12px; font-weight: 900; }
                .datetime-center {
                    text-align: center;
                    font-size: 12px;
                    font-weight: 700;
                    padding: 2px 0;
                    margin-bottom: 3px;
                }
                @media print {
                    body { 
                        margin: 0; 
                        padding: 3px;
                        transform: scale(0.90);
                        transform-origin: top center;
                        display: flex;
                        justify-content: center;
                        align-items: flex-start;
                        min-height: 100vh;
                    }
                    .no-print { display: none; }
                }
            </style>
        </head>
        <body>
            <div class="receipt-wrapper">
                <div class="receipt">
                    <div class="header-center">
                        ${businessLogo ? `<img src="${businessLogo}" alt="logo" class="header-logo" onerror="this.style.display='none'" />` : ''}
                        <div class="business-name">${escapeHtml(shopName)}</div>
                        ${businessPhone ? `<div class="business-phone">📞 ${escapeHtml(businessPhone)}</div>` : ''}
                        ${shopAddress ? `<div class="business-addr">${escapeHtml(shopAddress)}</div>` : ''}
                    </div>

                    <div class="receipt-title">${translations.purchaseReceipt || 'PURCHASE RECEIPT'}</div>
                    <div class="receipt-bill-number"><strong>${translations.billNumber || 'Bill No'}:</strong> #${escapeHtml(purchaseData.bill_no || '')}</div>
                    
                    <div class="receipt-customer">${translations.supplier || 'Supplier'}: <strong>${escapeHtml(supplierName)}</strong></div>
                    
                    <div class="datetime-center">
                        <strong>${translations.date || 'Date'}:</strong> ${receiptDateTime}
                    </div>

                    <table class="items-table">
                        <thead>
                            <tr>
                                <th style="width:8%;">#</th>
                                <th style="width:47%;">${translations.productName || 'Product'}</th>
                                <th style="width:15%;">${translations.quantity || 'Qty'}</th>
                                <th style="width:15%;">${translations.price || 'Price'}</th>
                                <th style="width:15%;">${translations.total || 'Total'}</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${purchaseData.items.map((item, idx) => `
                                <tr>
                                    <td>${idx + 1}</td>
                                    <td class="text-left"><div class="product-name">${escapeHtml(item.product_name || 'Product')}</div></td>
                                    <td>${parseFloat(item.quantity).toFixed(0)}</td>
                                    <td>${parseFloat(item.price).toFixed(0)}</td>
                                    <td>${parseFloat(item.total || item.price * item.quantity).toFixed(0)}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>

                    <div class="payment-box">
                        <div class="payment-row"><span>${translations.subtotal || 'Subtotal'}</span><span>${formatReceiptCurrency(grandTotal)}</span></div>
                        ${parseFloat(tax) > 0 ? `<div class="payment-row"><span>${translations.tax || 'Tax'}</span><span>${formatReceiptCurrency(tax)}</span></div>` : ''}
                        ${parseFloat(discount) > 0 ? `<div class="payment-row"><span>${translations.discount || 'Discount'}</span><span>-${formatReceiptCurrency(discount)}</span></div>` : ''}
                        <div class="payment-row total"><span>${translations.grandTotal || 'GRAND TOTAL'}</span><span>${formatReceiptCurrency(grandTotal)}</span></div>
                        ${parseFloat(paidAmount) > 0 ? `<div class="payment-row"><span>${translations.paidAmount || 'Paid'}</span><span>${formatReceiptCurrency(paidAmount)}</span></div>` : ''}
                        ${parseFloat(balance) > 0 ? `<div class="payment-row"><span>${translations.balance || 'Balance'}</span><span>${formatReceiptCurrency(balance)}</span></div>` : ''}
                        ${parseFloat(balance) <= 0 && parseFloat(paidAmount) > 0 ? `<div class="payment-row"><span>${translations.balance || 'Balance'}</span><span>${formatReceiptCurrency(balance)}</span></div>` : ''}
                    </div>

                    <div class="footer">
                        <div class="thankyou">${translations.thankYou || 'Thank you for your business!'}</div>
                        <div>Developed by OurZest PVT LTD.</div>
                    </div>
                </div>
            </div>

            <script>
                window.onload = function() {
                    setTimeout(function() {
                        window.print();
                        window.onafterprint = function() { window.close(); };
                    }, 400);
                };
            </script>
        </body>
        </html>
    `);

        printWindow.document.close();
        printWindow.focus();
    };

    const handleView = async (purchase) => {
        setSelectedPurchase(purchase);

        if (!purchase.items && purchase.id && currentShop) {
            try {
                const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        action: 'get_record',
                        id: purchase.id,
                        shop_id: currentShop.shop_id
                    })
                });
                const data = await response.json();
                if (data.status === 'success' && data.data) {
                    setSelectedPurchase(data.data);
                }
            } catch (error) {
                console.error('Error fetching purchase details:', error);
            }
        }

        setShowViewModal(true);
    };

    const handleEdit = async (purchase) => {
        if (Number(purchase.shop_id) !== Number(currentShop?.shop_id)) {
            showToast('error', 'You can only edit purchases from the current shop');
            return;
        }
        let purchaseData = purchase;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id: purchase.id, shop_id: currentShop.shop_id })
            });
            const result = await response.json();
            if (result.status !== 'success' || !result.data) throw new Error(result.message || 'Could not load purchase');
            purchaseData = result.data;
        } catch (error) {
            showToast('error', error.message || 'Could not load the purchase for editing');
            return;
        }
        const editableItems = (purchaseData.items || []).map(item => ({
            ...item,
            product_id: item.product_id || '',
            product_name: item.product_name || '',
            product_code: item.product_code || '',
            batch_no: item.batch_no || '',
            purchase_type: item.purchase_type || 'packing',
            items_per_pack: String(item.items_per_pack || 1),
            unit_of_measure: item.unit_of_measure || item.unit || 'piece',
            price: String(item.price ?? ''),
            cost_per_pack: String(item.cost_per_pack ?? ''),
            sell_per_pack: String(item.sell_per_pack ?? ''),
            profit_percentage: String(item.profit_percentage ?? ''),
            quantity: String(item.quantity ?? 1),
            expiry_date: item.expiry_date ? String(item.expiry_date).split('T')[0] : ''
        }));
        const totals = recalculateEditPurchase(editableItems, purchaseData.paid);
        setSelectedPurchase(purchaseData);
        setEditFormData({
            id: purchaseData.id,
            date: purchaseData.date ? purchaseData.date.split('T')[0] : '',
            bill_no: purchaseData.bill_no || '',
            supplierid: purchaseData.supplierid || '',
            supplier_name: purchaseData.supplier_name || '',
            phone: purchaseData.phone || '',
            address: purchaseData.address || '',
            paid: String(purchaseData.paid ?? 0),
            items: editableItems,
            ...totals
        });
        setEditFormErrors({});
        setShowEditModal(true);
    };

    const handleDeleteClick = (purchaseId) => {
        const purchase = purchases.find(p => p.id === purchaseId);
        if (!purchase || purchase.shop_id !== currentShop?.shop_id) {
            showToast('error', 'You can only delete purchases from the current shop');
            return;
        }
        setPurchaseToDelete(purchaseId);
        setShowDeleteModal(true);
    };

    const handleUpdatePurchase = useCallback(async (e) => {
        e.preventDefault();

        const errors = {};
        if (!editFormData.date) errors.date = 'Date is required';
        if (!editFormData.supplier_name) errors.supplier_name = 'Supplier name is required';
        if (!editFormData.supplierid) errors.supplier_name = 'Please select a valid supplier';
        if (!editFormData.bill_no?.trim()) errors.bill_no = 'Bill number is required';
        if (!editFormData.items.length) errors.items = 'At least one product is required';
        editFormData.items.forEach((item, index) => {
            if (!item.product_name?.trim() || !item.batch_no?.trim() || !(Number(item.quantity) > 0)) {
                errors.items = `Complete product name, batch and quantity on row ${index + 1}`;
            }
            const effectivePrice = item.purchase_type === 'sub_packing' ? item.price : (item.cost_per_pack || item.price);
            if (!(Number(effectivePrice) >= 0)) errors.items = `Enter a valid cost on row ${index + 1}`;
        });
        if (!editFormData.grand_total || parseFloat(editFormData.grand_total) <= 0) {
            errors.grand_total = 'Valid grand total is required';
        }
        if (editFormData.paid === undefined || editFormData.paid === '') {
            errors.paid = 'Paid amount is required';
        }

        if (Object.keys(errors).length > 0) {
            setEditFormErrors(errors);
            return;
        }

        setEditLoading(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'edit',
                    id: editFormData.id,
                    username: user.username,
                    shop_id: currentShop.shop_id,
                    supplierid: editFormData.supplierid,
                    date: editFormData.date,
                    bill_no: editFormData.bill_no,
                    supplier_name: editFormData.supplier_name,
                    phone: editFormData.phone || '',
                    address: editFormData.address || '',
                    grand_total: editFormData.items.reduce((sum, item) => sum + calculateEditLineTotal(item), 0),
                    paid: parseFloat(editFormData.paid) || 0,
                    balance: parseFloat(editFormData.balance) || 0,
                    items: editFormData.items.map(item => ({
                        ...item,
                        quantity: Number(item.quantity),
                        items_per_pack: Number(item.items_per_pack) || 1,
                        price: Number(item.price) || 0,
                        cost_per_pack: item.purchase_type === 'packing' ? Number(item.cost_per_pack || item.price) : null,
                        sell_per_pack: item.sell_per_pack === '' ? null : Number(item.sell_per_pack),
                        profit_percentage: item.profit_percentage === '' ? null : Number(item.profit_percentage)
                    })),
                    keep_existing_file: true
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                showToast('success', translations.purchaseUpdated || 'Purchase updated successfully');
                setShowEditModal(false);
                fetchPurchases();
            } else {
                showToast('error', data.message || translations.updateError || 'Error updating purchase');
            }
        } catch (error) {
            console.error('Error updating purchase:', error);
            showToast('error', translations.updateError || 'Error updating purchase');
        } finally {
            setEditLoading(false);
        }
    }, [editFormData, user, currentShop, translations, showToast, fetchPurchases, calculateEditLineTotal]);

    const handleDelete = async () => {
        if (!purchaseToDelete || !currentShop) return;
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/mechanics_module/mechanics_purchases.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete',
                    id: purchaseToDelete,
                    username: user.username,
                    shop_id: currentShop.shop_id
                })
            });
            const data = await response.json();
            if (data.status === 'success') {
                showToast('success', translations.deletedSuccess || 'Purchase deleted successfully');
                fetchPurchases();
                setShowDeleteModal(false);
                setPurchaseToDelete(null);
            } else {
                showToast('error', data.message || translations.deleteError || 'Error deleting purchase');
            }
        } catch (error) {
            console.error('Error deleting purchase:', error);
            showToast('error', translations.deleteError || 'Error deleting purchase');
        }
    };

    const handlePageChange = useCallback((page) => { if (page >= 1 && page <= pagination.totalPages) setCurrentPage(page); }, [pagination.totalPages]);

    const handleItemsPerPageChange = (e) => {
        setItemsPerPage(parseInt(e.target.value));
        setCurrentPage(1);
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    // Render shop indicator
    const renderShopIndicator = () => {
        if (!currentShop) {
            return (
                <div className="ozlistui-shop-indicator no-shop">
                    <div className="ozlistui-shop-badge">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        <span className="ozlistui-shop-warning">
                            {translations.noShopSelected || 'No shop selected'}
                        </span>
                    </div>
                    <button
                        className="ozlistui-btn ozlistui-btn-switch-shop"
                        onClick={() => setShowShopSelector(true)}
                    >
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.switchShop || 'Switch Shop'}</span>
                    </button>
                </div>
            );
        }

        return (
            <div className="ozlistui-shop-indicator active">
                {/* Shop indicator content */}
            </div>
        );
    };

    return (
        <div className={`ozlistui-container ${isRTL ? 'ozlistui-rtl' : 'ozlistui-ltr'} ${darkMode ? 'ozlistui-dark' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <ShopSelectorModal
                isOpen={showShopSelector}
                onClose={() => setShowShopSelector(false)}
                shops={availableShops}
                currentShop={currentShop}
                onSelectShop={handleSwitchShop}
                translations={translations}
                loading={loadingShops}
                darkMode={darkMode}
            />

            <div className="ozlistui-sticky-header">
                <div className="ozlistui-header-bg-animation">
                    <div className="gradient-orb orb-1"></div>
                    <div className="gradient-orb orb-2"></div>
                    <div className="gradient-orb orb-3"></div>
                    <div className="gradient-orb orb-4"></div>
                    <div className="gradient-orb orb-5"></div>
                </div>
                <div className="ozlistui-header-content">
                    <div className="ozlistui-header-left">
                        <div className="ozlistui-header-title">
                            <h1>{translations.purchaseList || 'Purchase List'}</h1>
                            <p>{translations.formDetails || 'All purchase details are below'}</p>
                        </div>
                    </div>
                    <div className="ozlistui-header-right">
                        <button
                            className="ozlistui-header-filter-btn"
                            onClick={() => setShowFilters(!showFilters)}
                            title={showFilters ? "Hide Filters" : "Show Filters"}
                        >
                            <FontAwesomeIcon icon={showFilters ? faEyeSlash : faFilter} />
                            <span>{showFilters ? "Hide Filters" : "Filters"}</span>
                        </button>

                        {!currentShop && (
                            <button className="ozlistui-header-shop-btn" onClick={() => setShowShopSelector(true)}>
                                <FontAwesomeIcon icon={faExchangeAlt} />
                                <span>{translations.switchShop || 'Switch Shop'}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {renderShopIndicator()}

            {showFilters && (
                <div className="ozlistui-filters-section">
                    <div className="ozlistui-filters-row">
                        <CustomDropdown
                            label={translations.filterBy || 'Filter By'}
                            icon={faTag}
                            value={filterBy}
                            onChange={setFilterBy}
                            options={filterByOptions}
                            translations={translations}
                        />

                        {filterBy === 'billNumber' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="billNumberFilter"
                                        value={billNumberFilter}
                                        onChange={(e) => setBillNumberFilter(e.target.value)}
                                        placeholder=" "
                                    />
                                    <label htmlFor="billNumberFilter">
                                        <FontAwesomeIcon icon={faTag} />
                                        {translations.billNumberFilter || 'Bill Number'}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'supplierName' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="supplierNameFilter"
                                        value={supplierNameFilter}
                                        onChange={(e) => setSupplierNameFilter(e.target.value)}
                                        placeholder=" "
                                    />
                                    <label htmlFor="supplierNameFilter">
                                        <FontAwesomeIcon icon={faUser} />
                                        {translations.supplierNameFilter || 'Supplier Name'}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'phone' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="tel"
                                        id="phoneFilter"
                                        value={phoneFilter}
                                        onChange={(e) => setPhoneFilter(e.target.value)}
                                        placeholder=" "
                                        dir="ltr"
                                    />
                                    <label htmlFor="phoneFilter">
                                        <FontAwesomeIcon icon={faPhone} />
                                        {translations.phoneFilter || 'Phone Number'}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'added_by' && (
                            <div className="ozlistui-filter-group">
                                <div className="ozlistui-floating-wrapper">
                                    <input
                                        type="text"
                                        id="addedByFilter"
                                        value={addedByFilter}
                                        onChange={(e) => setAddedByFilter(e.target.value)}
                                        placeholder=" "
                                    />
                                    <label htmlFor="addedByFilter">
                                        <FontAwesomeIcon icon={faUser} />
                                        {translations.addedByFilter || 'Added By'}
                                    </label>
                                </div>
                            </div>
                        )}

                        {filterBy === 'date' && (
                            <>
                                <div className="ozlistui-filter-group ozlistui-date-filter">
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="date"
                                            id="startDate"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            max={getTodayDate()}
                                            placeholder=" "
                                        />
                                        <label htmlFor="startDate">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            {translations.startDate || 'Start Date'}
                                        </label>
                                    </div>
                                </div>
                                <div className="ozlistui-filter-group ozlistui-date-filter">
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="date"
                                            id="endDate"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            min={startDate}
                                            max={getTodayDate()}
                                            placeholder=" "
                                        />
                                        <label htmlFor="endDate">
                                            <FontAwesomeIcon icon={faCalendar} />
                                            {translations.endDate || 'End Date'}
                                        </label>
                                    </div>
                                </div>
                            </>
                        )}

                        {filterBy === 'paymentStatus' && (
                            <CustomDropdown
                                label={translations.paymentStatusFilter || 'Payment Status'}
                                icon={faCreditCard}
                                value={paymentStatusFilter}
                                onChange={setPaymentStatusFilter}
                                options={paymentStatusOptions}
                                translations={translations}
                            />
                        )}

                        <CustomDropdown
                            label={translations.orientation || 'Orientation'}
                            icon={faFilePdf}
                            value={pdfOrientation}
                            onChange={setPdfOrientation}
                            options={pdfOrientationOptions}
                            translations={translations}
                        />

                        <CustomDropdown
                            label={translations.pageSize || 'Page Size'}
                            icon={faFilePdf}
                            value={pdfPageSize}
                            onChange={setPdfPageSize}
                            options={pdfPageSizeOptions}
                            translations={translations}
                        />

                        <CustomDropdown
                            label="Page Range"
                            icon={faFilePdf}
                            value={pdfPageRange}
                            onChange={setPdfPageRange}
                            options={pdfPageRangeOptions}
                            translations={translations}
                        />

                        {pdfPageRange === 'range' && (
                            <>
                                <div className="ozlistui-filter-group" style={{ width: '70px' }}>
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="number"
                                            min="1"
                                            value={pdfStartPage}
                                            onChange={(e) => setPdfStartPage(Math.max(1, parseInt(e.target.value) || 1))}
                                            placeholder=" "
                                        />
                                        <label>From</label>
                                    </div>
                                </div>
                                <div className="ozlistui-filter-group" style={{ width: '70px' }}>
                                    <div className="ozlistui-floating-wrapper">
                                        <input
                                            type="number"
                                            min={pdfStartPage}
                                            value={pdfEndPage}
                                            onChange={(e) => setPdfEndPage(Math.max(pdfStartPage, parseInt(e.target.value) || pdfStartPage))}
                                            placeholder=" "
                                        />
                                        <label>To</label>
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="ozlistui-filter-group">
                            <button onClick={fetchPurchases} className="ozlistui-refresh-btn" disabled={loading}>
                                <FontAwesomeIcon icon={faRefresh} />
                                <span className="ozlistui-btn-text">{translations.refresh || 'Refresh'}</span>
                            </button>
                        </div>

                        <div className="ozlistui-filter-group">
                            <button
                                onClick={exportToPDF}
                                className="ozlistui-refresh-btn"
                                disabled={filteredPurchases.length === 0 || pdfLoading}
                                style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', color: 'white' }}
                            >
                                {pdfLoading ? <div className="ozlistui-btn-spinner-small"></div> : <FontAwesomeIcon icon={faFilePdf} />}
                                <span className="ozlistui-btn-text">{translations.exportToPDF || 'Export PDF'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="ozlistui-loading-container">
                    <div className="ozlistui-loading-spinner"></div>
                    <p>{translations.loadingUserData || 'Loading your purchase data...'}</p>
                </div>
            ) : !currentShop ? (
                <div className="ozlistui-no-shop-container">
                    <div className="ozlistui-no-shop-message">
                        <FontAwesomeIcon icon={faStore} />
                        <h3>{translations.noShopSelected || 'No Shop Selected'}</h3>
                        <p>{translations.selectShop || 'Please select a shop to view purchases'}</p>
                        <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => setShowShopSelector(true)}>
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <span>{translations.switchShop || 'Switch Shop'}</span>
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <div className="ozlistui-table-scroll">
                        <div className="ozlistui-table-container">
                            <div className="ozlistui-table-wrapper">
                                <table className="ozlistui-table">
                                    <thead>
                                        <tr>
                                            <th className="ozlistui-id-column ozlistui-centered-cell">{translations.serialNo || 'S.No'}</th>
                                            <th className="ozlistui-centered-cell">{translations.date || 'Date'}</th>
                                            <th className="ozlistui-centered-cell">{translations.billNumber || 'Bill No'}</th>
                                            <th className="ozlistui-centered-cell">{translations.supplierName || 'Supplier'}</th>
                                            <th className="ozlistui-centered-cell">{translations.grandTotal || 'Total'}</th>
                                            <th className="ozlistui-centered-cell">{translations.paidAmount || 'Paid'}</th>
                                            <th className="ozlistui-centered-cell">{translations.balance || 'Balance'}</th>
                                            <th className="ozlistui-centered-cell">{translations.paymentStatus || 'Status'}</th>
                                            <th className="ozlistui-centered-cell">{translations.addedBy || 'Added By'}</th>
                                            <th className="ozlistui-actions-column ozlistui-centered-cell">{translations.actions || 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {pagination.currentPurchases.map((purchase, index) => {
                                            const paymentStatus = getPaymentStatus(purchase.paid, purchase.grand_total);
                                            const addedBy = purchase.username || '-';
                                            const isSelfAdded = addedBy === user?.username;

                                            return (
                                                <tr key={purchase.id}>
                                                    <td className="ozlistui-id-cell ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{pagination.startIndex + index + 1}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{formatDate(purchase.date)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text ozlistui-bill-number" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{purchase.bill_no}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text">{purchase.supplier_name || '-'}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ color: '#059669', fontWeight: 'bold' }}>{formatCurrency(purchase.grand_total)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ color: '#3b82f6', fontWeight: 'bold' }}>{formatCurrency(purchase.paid)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className="ozlistui-cell-text" style={{ color: parseFloat(purchase.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}>{formatCurrency(purchase.balance)}</span>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <div className="ozlistui-payment-badge" style={{ backgroundColor: `${paymentStatus.color}20`, color: paymentStatus.color, border: `1px solid ${paymentStatus.color}40`, display: 'inline-block', padding: '0.2rem 0.6rem', fontSize: '0.7rem' }}>
                                                            {paymentStatus.text}
                                                        </div>
                                                    </td>
                                                    <td className="ozlistui-centered-cell">
                                                        <span className={`ozlistui-added-by-badge ${isSelfAdded ? 'ozlistui-self' : ''}`}>
                                                            {addedBy}
                                                        </span>
                                                    </td>
                                                    <td className="ozlistui-actions-cell ozlistui-centered-cell">
                                                        <div className="ozlistui-actions-content">
                                                            <button onClick={() => handleView(purchase)} className="ozlistui-action-btn ozlistui-view-btn" title={translations.view || 'View'}>
                                                                <FontAwesomeIcon icon={faEye} />
                                                            </button>
                                                            <button onClick={() => handlePrintBill(purchase)} className="ozlistui-action-btn ozlistui-print-btn" title={translations.print || 'Print'}>
                                                                <FontAwesomeIcon icon={faPrint} />
                                                            </button>
                                                            <button onClick={() => handleEdit(purchase)} className="ozlistui-action-btn ozlistui-edit-btn" title={translations.edit || 'Edit'} disabled={!isSelfAdded} style={{ opacity: isSelfAdded ? 1 : 0.5, cursor: isSelfAdded ? 'pointer' : 'not-allowed' }}>
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button onClick={() => handleDeleteClick(purchase.id)} className="ozlistui-action-btn ozlistui-delete-btn" title={translations.delete || 'Delete'} disabled={!isSelfAdded} style={{ opacity: isSelfAdded ? 1 : 0.5, cursor: isSelfAdded ? 'pointer' : 'not-allowed' }}>
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    {filteredPurchases.length > 0 && currentShop && (
                        <div className="ozlistui-pagination">
                            <div className="ozlistui-pagination-left">
                                <div className="ozlistui-per-page">
                                    <span>{translations.show || 'Show'}</span>
                                    <select
                                        value={itemsPerPage}
                                        onChange={(e) => {
                                            setItemsPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                        className="ozlistui-per-page-select"
                                    >
                                        <option value={20}>20</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                        <option value={500}>500</option>
                                        <option value={1000}>1000</option>
                                    </select>
                                    <span>{translations.perPage || 'per page'}</span>
                                </div>
                                <div className="ozlistui-pagination-info">
                                    <span>
                                        {translations.showing || 'Showing'}
                                        <strong> {pagination.startIndex + 1} </strong>
                                        {translations.to || 'to'}
                                        <strong> {Math.min(pagination.startIndex + itemsPerPage, filteredPurchases.length)} </strong>
                                        {translations.of || 'of'}
                                        <strong> {filteredPurchases.length} </strong>
                                        {translations.purchases || 'purchases'}
                                    </span>
                                </div>
                            </div>
                            <div className="ozlistui-pagination-right">
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="ozlistui-pagination-btn"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} />
                                    <span>{translations.previous || 'Previous'}</span>
                                </button>
                                <div className="ozlistui-page-numbers">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = currentPage - 2 + i;
                                        }
                                        return (
                                            <button key={pageNum} onClick={() => handlePageChange(pageNum)} className={`ozlistui-page-number ${currentPage === pageNum ? 'ozlistui-active' : ''}`}>
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>
                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === pagination.totalPages}
                                    className="ozlistui-pagination-btn"
                                >
                                    <span>{translations.next || 'Next'}</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* View Modal */}
            {showViewModal && selectedPurchase && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={() => setShowViewModal(false)}>
                    <div className="ozlistui-modal-content ozlistui-view-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="ozlistui-modal-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faReceipt} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{translations.purchaseDetails || 'Purchase Details'}</h3>
                                <span className="ozlistui-purchase-number-badge">#{selectedPurchase.bill_no}</span>
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowViewModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="ozlistui-modal-body ozlistui-view-modal-body">
                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faInfoCircle} />Purchase Information</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.purchaseId || 'Purchase ID'}:</span><span className="ozlistui-view-value">#{selectedPurchase.id}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.date || 'Date'}:</span><span className="ozlistui-view-value">{formatDate(selectedPurchase.date)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.billNumber || 'Bill Number'}:</span><span className="ozlistui-view-value" style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{selectedPurchase.bill_no}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.supplierName || 'Supplier Name'}:</span><span className="ozlistui-view-value">{selectedPurchase.supplier_name || '-'}</span></div>
                                    {selectedPurchase.phone && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.phone || 'Phone'}:</span><span className="ozlistui-view-value">{selectedPurchase.phone}</span></div>
                                    )}
                                    {selectedPurchase.address && (
                                        <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.supplierAddress || 'Address'}:</span><span className="ozlistui-view-value">{selectedPurchase.address}</span></div>
                                    )}
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.addedBy || 'Added By'}:</span><span className="ozlistui-view-value"><span className="ozlistui-added-by-badge">{selectedPurchase.username || '-'}</span></span></div>
                                    {selectedPurchase.notes && (
                                        <div className="ozlistui-view-row ozlistui-full-width"><span className="ozlistui-view-label">{translations.notes || 'Notes'}:</span><span className="ozlistui-view-value">{selectedPurchase.notes}</span></div>
                                    )}
                                </div>
                            </div>

                            <div className="ozlistui-view-section">
                                <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faCalculator} />Payment Summary</h4>
                                <div className="ozlistui-view-grid">
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.subtotal || 'Subtotal'}:</span><span className="ozlistui-view-value">{formatCurrency(selectedPurchase.subtotal)}</span></div>
                                    {parseFloat(selectedPurchase.tax) > 0 && <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.tax || 'Tax'}:</span><span className="ozlistui-view-value">{formatCurrency(selectedPurchase.tax)}</span></div>}
                                    {parseFloat(selectedPurchase.discount) > 0 && <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.discount || 'Discount'}:</span><span className="ozlistui-view-value">-{formatCurrency(selectedPurchase.discount)}</span></div>}
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.grandTotal || 'Grand Total'}:</span><span className="ozlistui-view-value" style={{ color: '#059669', fontWeight: 'bold' }}>{formatCurrency(selectedPurchase.grand_total)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.paidAmount || 'Paid Amount'}:</span><span className="ozlistui-view-value" style={{ color: '#3b82f6', fontWeight: 'bold' }}>{formatCurrency(selectedPurchase.paid)}</span></div>
                                    <div className="ozlistui-view-row"><span className="ozlistui-view-label">{translations.balance || 'Balance'}:</span><span className="ozlistui-view-value" style={{ color: parseFloat(selectedPurchase.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}>{formatCurrency(selectedPurchase.balance)}</span></div>
                                </div>
                            </div>

                            {selectedPurchase.items && selectedPurchase.items.length > 0 && (
                                <div className="ozlistui-view-section">
                                    <h4 className="ozlistui-view-section-title"><FontAwesomeIcon icon={faBoxes} />{translations.items || 'Items'}</h4>
                                    <div className="ozlistui-items-table-container">
                                        <div className="ozlistui-items-table-wrapper">
                                            <table className="ozlistui-items-table">
                                                <thead>
                                                    <tr>
                                                        <th>{translations.srNo || 'Sr#'}</th>
                                                        <th>{translations.batch || 'Batch'}</th>
                                                        <th>{translations.productName || 'Product'}</th>
                                                        <th>{translations.category || 'Category'}</th>
                                                        <th>{translations.barcode || 'Barcode'}</th>
                                                        <th className="ozlistui-text-right">{translations.costPrice || 'Cost Price'}</th>
                                                        <th className="ozlistui-text-right">{translations.sellPrice || 'Sell Price'}</th>
                                                        <th className="ozlistui-text-right">{translations.quantity || 'Qty'}</th>
                                                        <th className="ozlistui-text-right">{translations.stock || 'Stock'}</th>
                                                        <th className="ozlistui-text-right">{translations.total || 'Total'}</th>
                                                        <th>{translations.expiry || 'Expiry'}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {selectedPurchase.items.map((item, idx) => {
                                                        const itemTotal = (parseFloat(item.price) || 0) * (parseFloat(item.quantity) || 0);
                                                        const stockPacks = parseFloat(item.available_stock_packs || item.quantity || 0);
                                                        const stockUnits = parseFloat(item.bquantity ? parseBQuantityNumeric(item.bquantity) : 0);
                                                        const isPackingMode = (item.purchase_type || 'packing') === 'packing';
                                                        const stockDisplay = isPackingMode
                                                            ? stockPacks.toFixed(0)
                                                            : stockUnits.toFixed(2);

                                                        return (
                                                            <tr key={idx}>
                                                                <td>{idx + 1}</td>
                                                                <td>{item.batch_no || '-'}</td>
                                                                <td><strong>{item.product_name || '-'}</strong></td>
                                                                <td>{item.category || '-'}</td>
                                                                <td style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.barcode || '-'}</td>
                                                                <td className="ozlistui-text-right">{formatCurrency(item.price)}</td>
                                                                <td className="ozlistui-text-right">{item.sell_per_pack ? formatCurrency(item.sell_per_pack) : '-'}</td>
                                                                <td className="ozlistui-text-right">{parseFloat(item.quantity).toFixed(2)}</td>
                                                                <td className="ozlistui-text-right">{stockDisplay}</td>
                                                                <td className="ozlistui-text-right ozlistui-total-cell">{formatCurrency(itemTotal)}</td>
                                                                <td>{item.expiry_date ? new Date(item.expiry_date).toLocaleDateString() : '-'}</td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowViewModal(false)}>{translations.close || 'Close'}</button>
                            <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handlePrintBill(selectedPurchase); }}>
                                <FontAwesomeIcon icon={faPrint} />
                                {translations.print || 'Print'}
                            </button>
                            <button className="ozlistui-btn ozlistui-btn-primary" onClick={() => { setShowViewModal(false); handleEdit(selectedPurchase); }}>
                                <FontAwesomeIcon icon={faEdit} />
                                {translations.edit || 'Edit'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && selectedPurchase && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={(e) => e.stopPropagation()}>
                    <div
                        ref={modalRef}
                        className="ozlistui-modal-content ozlistui-edit-modal-content"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'fixed',
                            left: modalPosition.x || '50%',
                            top: modalPosition.y || '50%',
                            transform: modalPosition.x === 0 && modalPosition.y === 0 ? 'translate(-50%, -50%)' : 'none',
                            width: modalSize.width,
                            minWidth: '350px',
                            maxWidth: '95vw',
                            maxHeight: '85vh'
                        }}
                    >
                        <div className="ozlistui-resize-handle-top" onMouseDown={(e) => startResize(e, 'n')}></div>
                        <div className="ozlistui-resize-handle-bottom" onMouseDown={(e) => startResize(e, 's')}></div>
                        <div className="ozlistui-resize-handle-left" onMouseDown={(e) => startResize(e, 'w')}></div>
                        <div className="ozlistui-resize-handle-right" onMouseDown={(e) => startResize(e, 'e')}></div>
                        <div className="ozlistui-resize-handle-nw" onMouseDown={(e) => startResize(e, 'nw')}></div>
                        <div className="ozlistui-resize-handle-ne" onMouseDown={(e) => startResize(e, 'ne')}></div>
                        <div className="ozlistui-resize-handle-sw" onMouseDown={(e) => startResize(e, 'sw')}></div>
                        <div className="ozlistui-resize-handle-se" onMouseDown={(e) => startResize(e, 'se')}></div>

                        <div className="ozlistui-modal-header" onMouseDown={startDrag}>
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faEdit} /></div>
                            <div className="ozlistui-modal-title">
                                <h3>{translations.editPurchase || 'Edit Purchase'}</h3>
                                <span className="ozlistui-purchase-number-badge">#{selectedPurchase.bill_no}</span>
                            </div>
                            <div className="ozlistui-width-control-group">
                                {Object.entries(widthModes).map(([key, mode]) => (
                                    <button key={key} className={`ozlistui-width-btn ${currentWidthMode === key ? 'ozlistui-active' : ''}`} onClick={() => setWidthMode(key)} title={mode.label}>
                                        <FontAwesomeIcon icon={mode.icon} />
                                    </button>
                                ))}
                            </div>
                            <button className="ozlistui-close-btn" onClick={() => setShowEditModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>

                        <form onSubmit={handleUpdatePurchase}>
                            <div className="ozlistui-modal-body">
                                <div className="ozlistui-form-grid">
                                    <div className={`ozlistui-form-group ${editFormErrors.date ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="date"
                                                id="edit_date"
                                                name="date"
                                                value={editFormData.date}
                                                onChange={(e) => setEditFormData({ ...editFormData, date: e.target.value })}
                                                max={getTodayDate()}
                                                placeholder=" "
                                                className={editFormErrors.date ? 'ozlistui-error' : ''}
                                            />
                                            <label htmlFor="edit_date">
                                                <FontAwesomeIcon icon={faCalendar} />
                                                {translations.date || 'Date'} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.date && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.date}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.bill_no ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="text"
                                                id="edit_bill_no"
                                                name="bill_no"
                                                value={editFormData.bill_no}
                                                onChange={(e) => setEditFormData({ ...editFormData, bill_no: e.target.value })}
                                                placeholder=" "
                                            />
                                            <label htmlFor="edit_bill_no">
                                                <FontAwesomeIcon icon={faFileInvoice} />
                                                {translations.billNumber || 'Bill Number'} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.bill_no && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.bill_no}
                                            </div>
                                        )}
                                    </div>

                                    <div className={`ozlistui-form-group ${editFormErrors.supplier_name ? 'ozlistui-has-error' : ''}`}>
                                        <div className="ozlistui-input-wrapper">
                                            <select
                                                id="edit_supplier_name"
                                                name="supplier_name"
                                                value={String(editFormData.supplierid || '')}
                                                onChange={(e) => {
                                                    const supplier = editSuppliers.find(option => String(option.supplierid) === e.target.value);
                                                    setEditFormData(prev => ({
                                                        ...prev,
                                                        supplierid: e.target.value,
                                                        supplier_name: supplier?.supplier_name || supplier?.name || '',
                                                        phone: supplier?.phone || '',
                                                        address: supplier?.address || ''
                                                    }));
                                                }}
                                            >
                                                <option value="">Select supplier</option>
                                                {editSuppliers.map(supplier => <option key={supplier.supplierid} value={supplier.supplierid}>{supplier.supplier_name || supplier.name}</option>)}
                                            </select>
                                            <label htmlFor="edit_supplier_name">
                                                <FontAwesomeIcon icon={faUser} />
                                                {translations.supplierName || 'Supplier Name'} <span className="ozlistui-required">*</span>
                                            </label>
                                        </div>
                                        {editFormErrors.supplier_name && (
                                            <div className="ozlistui-error-message">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {editFormErrors.supplier_name}
                                            </div>
                                        )}
                                    </div>

                                    <div className="ozlistui-form-group">
                                        <div className="ozlistui-input-wrapper">
                                            <input
                                                type="tel"
                                                id="edit_phone"
                                                name="phone"
                                                value={editFormData.phone}
                                                onChange={(e) => setEditFormData({ ...editFormData, phone: e.target.value })}
                                                placeholder=" "
                                                dir="ltr"
                                            />
                                            <label htmlFor="edit_phone">
                                                <FontAwesomeIcon icon={faPhone} />
                                                {translations.phone || 'Phone Number'}
                                                <span> ({translations.optional || 'Optional'})</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group ozlistui-full-width">
                                        <div className="ozlistui-input-wrapper">
                                            <textarea
                                                id="edit_address"
                                                name="address"
                                                value={editFormData.address}
                                                onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                                                rows="2"
                                                placeholder=" "
                                            ></textarea>
                                            <label htmlFor="edit_address">
                                                <FontAwesomeIcon icon={faMapMarkerAlt} />
                                                {translations.supplierAddress || 'Address'}
                                                <span> ({translations.optional || 'Optional'})</span>
                                            </label>
                                        </div>
                                    </div>

                                    <div className="ozlistui-form-group ozlistui-full-width">
                                        <div className="ozui-form-row" style={{ marginBottom: 14 }}>
                                            <div className="ozui-field" ref={editProductSearchRef} style={{ position: 'relative', width: '100%' }}>
                                                <div className="ozui-input-wrapper">
                                                    <input type="text" id="edit_global_product_search" value={editProductSearch}
                                                        onChange={e => searchProductsForEdit(e.target.value)}
                                                        onFocus={() => editProductResults.length && setShowEditProductResults(true)}
                                                        placeholder=" " autoComplete="off" disabled={editLoading} />
                                                    <label htmlFor="edit_global_product_search"><FontAwesomeIcon icon={faSearch} /> {translations.searchProducts || 'Search Products'}</label>
                                                    {searchingEditProducts && <FontAwesomeIcon icon={faSpinner} spin className="ozui-search-indicator" />}
                                                </div>
                                                {showEditProductResults && editProductResults.length > 0 && (
                                                    <div className="ozui-product-dropdown">
                                                        {editProductResults.map(product => (
                                                            <div key={product.id} className="ozui-product-item" onMouseDown={e => e.preventDefault()} onClick={() => addProductToEdit(product)}>
                                                                <div className="ozui-product-item-name">{product.product_name}{product.product_code && <span className="ozui-product-code-badge">{product.product_code}</span>}</div>
                                                                <div className="ozui-product-item-details">
                                                                    {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>}
                                                                    {product.category && <span><FontAwesomeIcon icon={faBox} /> {product.category}</span>}
                                                                    {product.barcode && <span><FontAwesomeIcon icon={faBarcode} /> {product.barcode}</span>}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="ozlistui-edit-items-scroll-region" role="region" aria-label="Editable purchase products" tabIndex="0">
                                            <PurchaseItemsTable 
                                                items={editFormData.items} 
                                                onItemChange={updateEditItem}
                                                onRemove={removeEditItem} 
                                                onAdd={addEditItem} 
                                                errors={editFormErrors}
                                                disabled={editLoading} 
                                                translations={translations} 
                                                isRTL={isRTL}
                                                user={user} 
                                                shopId={currentShop?.shop_id} 
                                                showToast={showToast} 
                                            />
                                        </div>
                                        {editFormErrors.items && <div className="ozlistui-error-message"><FontAwesomeIcon icon={faExclamationTriangle} /> {editFormErrors.items}</div>}
                                    </div>

                                    <div className="ozlistui-edit-financial-summary">
                                        <div className={`ozlistui-form-group ${editFormErrors.grand_total ? 'ozlistui-has-error' : ''}`}>
                                            <div className="ozlistui-input-wrapper">
                                                <input
                                                    type="number"
                                                    id="edit_grand_total"
                                                    name="grand_total"
                                                    value={editFormData.grand_total}
                                                    readOnly
                                                    step="0.01"
                                                    min="0"
                                                    className="ozlistui-readonly-input"
                                                    placeholder="0.00"
                                                />
                                                <label htmlFor="edit_grand_total">
                                                    <FontAwesomeIcon icon={faDollarSign} />
                                                    {translations.grandTotal || 'Grand Total'} <span className="ozlistui-required">*</span>
                                                </label>
                                            </div>
                                            {editFormErrors.grand_total && (
                                                <div className="ozlistui-error-message">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {editFormErrors.grand_total}
                                                </div>
                                            )}
                                        </div>

                                        <div className={`ozlistui-form-group ${editFormErrors.paid ? 'ozlistui-has-error' : ''}`}>
                                            <div className="ozlistui-input-wrapper">
                                                <input
                                                    type="number"
                                                    id="edit_paid"
                                                    name="paid"
                                                    value={editFormData.paid}
                                                    onChange={(e) => {
                                                        const paid = e.target.value;
                                                        setEditFormData(prev => {
                                                            return { ...prev, paid, ...recalculateEditPurchase(prev.items, paid) };
                                                        });
                                                    }}
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="0.00"
                                                />
                                                <label htmlFor="edit_paid">
                                                    <FontAwesomeIcon icon={faMoneyBill} />
                                                    {translations.paidAmount || 'Paid Amount'} <span className="ozlistui-required">*</span>
                                                </label>
                                            </div>
                                            {editFormErrors.paid && (
                                                <div className="ozlistui-error-message">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {editFormErrors.paid}
                                                </div>
                                            )}
                                        </div>

                                        <div className="ozlistui-form-group ozlistui-balance-display">
                                            <div className="ozlistui-input-wrapper">
                                                <input
                                                    type="text"
                                                    id="edit_balance"
                                                    value={formatCurrency(editFormData.balance)}
                                                    readOnly
                                                    className="ozlistui-readonly-input"
                                                    style={{ color: parseFloat(editFormData.balance) > 0 ? '#d97706' : '#059669', fontWeight: 'bold' }}
                                                />
                                                <label htmlFor="edit_balance">
                                                    <FontAwesomeIcon icon={faBalanceScale} />
                                                    {translations.balance || 'Balance'}
                                                </label>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="ozlistui-modal-footer">
                                <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowEditModal(false)}>
                                    <FontAwesomeIcon icon={faTimes} />
                                    {translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="ozlistui-btn ozlistui-btn-primary" disabled={editLoading}>
                                    {editLoading ? (
                                        <>
                                            <div className="ozlistui-btn-spinner"></div>
                                            <span>{translations.updating || 'Updating...'}</span>
                                        </>
                                    ) : (
                                        <>
                                            <FontAwesomeIcon icon={faSave} />
                                            <span>{translations.saveChanges || 'Save Changes'}</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className={`ozlistui-modal-overlay ${darkMode ? 'ozlistui-dark' : ''}`} onClick={(e) => e.stopPropagation()}>
                    <div className="ozlistui-modal-content ozlistui-delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ozlistui-modal-header ozlistui-delete-header">
                            <div className="ozlistui-modal-icon"><FontAwesomeIcon icon={faExclamationTriangle} /></div>
                            <div className="ozlistui-modal-title"><h3>{translations.confirmDelete || 'Confirm Delete'}</h3></div>
                            <button className="ozlistui-close-btn" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} /></button>
                        </div>
                        <div className="ozlistui-modal-body">
                            <div className="ozlistui-delete-message">
                                <p>{translations.deleteMessage || 'Are you sure you want to delete this purchase?'}</p>
                                <p><FontAwesomeIcon icon={faExclamationTriangle} /> {translations.deleteWarning || 'This action cannot be undone.'}</p>
                            </div>
                        </div>
                        <div className="ozlistui-modal-footer">
                            <button type="button" className="ozlistui-btn ozlistui-btn-secondary" onClick={() => setShowDeleteModal(false)}><FontAwesomeIcon icon={faTimes} />{translations.noCancel || 'No, Cancel'}</button>
                            <button type="button" className="ozlistui-btn ozlistui-btn-delete" onClick={handleDelete}><FontAwesomeIcon icon={faTrash} />{translations.yesDelete || 'Yes, Delete'}</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CosmeticsPurchaseList;